import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, type RecipientInfo, type RecipientMethod, type PaymentQuote, type TransferResult, type WalletBankPaymentQuote, type ApiError } from '../services/apiService';
import { beneficiaryService, resolveContactMobile, type BeneficiaryAccount, type PaymentBeneficiary } from '../services/beneficiaryService';
import { SaveContactPrompt } from '../components/SaveContactPrompt';
import { findContactByMsisdn, findContactByBankAccount, contactMatchesMsisdn, msisdnEquals } from '../utils/saMsisdn';
import {
  assertInstantPaymentAccountNotCardPan,
  bankFieldModalCopy,
  buildInstantPaymentNotices,
  INSTANT_PAYMENT_RAIL_HINT,
  validateSaBankAccountNumber,
  walletDestinationModalCopy,
} from '../utils/saBankAccountValidation';
import { getToken } from '../utils/authToken';
import { APP_CONFIG } from '../config/app-config';
import { validateMobileNumber } from '../services/overlayService';
import { logError, logErrorWithContext } from '../services/loggingService';
import { isWalletUatEnvironment } from '../utils/environment';
import { formatCurrency as formatCurrencyShared, randsToCents } from '../utils/formatCurrency';
import { pollEftPaymentStatus, eftStatusModalCopy } from '../utils/eftPaymentPolling';
import { collapseDeclinedInstantPayments } from '../utils/transactionDisplay';
import {
  canProceedWithBankPayment,
  computeMaxAffordableAmount,
  isOverAffordableLimit,
  parseWalletBalanceZar,
} from '../utils/bankPaymentAffordability';
import { BankPaymentFeeAdjustModal } from '../components/overlays/shared/BankPaymentFeeAdjustModal';
import { BankPaymentAmountHints } from '../components/overlays/shared/BankPaymentAmountHints';
import { validateRecipientIdentity } from '../utils/recipientIdentityValidation';

// Import centralized transaction icon utility
import { getTransactionIcon } from '../utils/transactionIcons.tsx';

import { 
  Search, 
  Plus, 
  PlusCircle,
  ArrowLeft,
  Building2,
  Send,
  Star,
  Loader2,
  Wallet,
  Globe,
  ChevronRight,
  ChevronDown,
  Edit2,
  Trash2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { ConfirmationModal } from '../components/overlays/shared/ConfirmationModal';
import { PaymentProceedModal, type PaymentProceedPayload } from '../components/overlays/shared/PaymentProceedModal';
import { ErrorModal } from '../components/ui/ErrorModal';
import { AddAccountModal, CHANNELS_BY_COUNTRY, MOOLAHMOVE_COUNTRIES } from '../components/overlays/shared/AddAccountModal';

// Beneficiary Types - Using centralized service types
// Note: SendMoneyPage only handles PaymentBeneficiary types (mymoolah and bank)
// Service-specific beneficiaries (airtime, electricity, biller) are handled separately

// Transaction Types (frontend view)
interface Transaction {
  id: string;
  beneficiaryId?: string;
  beneficiaryName: string;
  amount: number;
  date: Date;
  status: 'completed' | 'pending' | 'failed';
  reference?: string;
  accountType: 'mymoolah' | 'bank';
  description?: string;
  kind?: 'send' | 'other';
  receiverWalletId?: string;
  type: 'send' | 'receive' | 'deposit' | 'withdraw' | 'transfer' | 'payment' | 'refund' | 'fee';
  senderWalletId?: string;
  metadata?: any;
}

interface FetchedWalletTransaction {
  id: string;
  type: 'received' | 'sent';
  amount: number;
  currency: string;
  description: string;
  date: string;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed';
  counterparty: string;
  senderWalletId?: string;
  receiverWalletId?: string;
  metadata?: any;
}

// South African banks supported for bank transfers.
const SA_BANKS = [
  { code: 'ABSA', name: 'ABSA Bank', branchCode: '632005' },
  { code: 'AFRICAN', name: 'African Bank', branchCode: '430000' },
  { code: 'BIDVEST', name: 'Bidvest Bank', branchCode: '462005' },
  { code: 'CAPITEC', name: 'Capitec Bank', branchCode: '470010' },
  { code: 'DISCOVERY', name: 'Discovery Bank', branchCode: '679000' },
  { code: 'FNB', name: 'First National Bank', branchCode: '250655' },
  { code: 'INVESTEC', name: 'Investec Bank', branchCode: '580105' },
  { code: 'NEDBANK', name: 'Nedbank', branchCode: '198765' },
  { code: 'POSTBANK', name: 'Postbank', branchCode: '460005' },
  { code: 'STANDARD', name: 'Standard Bank', branchCode: '051001' },
  { code: 'TYMEBANK', name: 'TymeBank', branchCode: '678910' }
];

const MOOLAHMOVE_ENABLED = isWalletUatEnvironment();

// No mock data; pull from backend

// Function to clean up transaction descriptions - remove "Ref:" prefix
function getCleanTransactionText(description: string): string {
  const cleanDescription = description || '';
  
  // Remove "Ref:" prefix and extract the actual description
          // Convert transaction description format
  if (cleanDescription.includes('| Ref:')) {
    // Extract the name part (before the pipe)
    const namePart = cleanDescription.split('|')[0].trim();
    
    // Extract the description part (after "Ref:")
    const refPart = cleanDescription.split('| Ref:')[1] || '';
    const actualDescription = refPart.trim();
    
    // Limit description to 20 characters
    const truncatedDescription = actualDescription.length > 20 
      ? actualDescription.substring(0, 20) + '...' 
      : actualDescription;
    
    return `${namePart} | ${truncatedDescription}`;
  }
  
  // Also handle variations like "Ref:" without the pipe
  if (cleanDescription.includes('Ref:')) {
    // Extract the name part (before "Ref:")
    const namePart = cleanDescription.split('Ref:')[0].trim();
    
    // Extract the description part (after "Ref:")
    const refPart = cleanDescription.split('Ref:')[1] || '';
    const actualDescription = refPart.trim();
    
    // Limit description to 20 characters
    const truncatedDescription = actualDescription.length > 20 
      ? actualDescription.substring(0, 20) + '...' 
      : actualDescription;
    
    return `${namePart}${truncatedDescription}`;
  }
  
  return cleanDescription;
}

function digitsOnly(value?: string | null): string {
  return String(value || '').replace(/\D/g, '');
}

/** Compare SA mobiles without treating 082… and +2782… as a change. */
function comparableMsisdn(value?: string | null): string {
  const digits = digitsOnly(value);
  if (digits.startsWith('27') && digits.length === 11) return digits.slice(2);
  if (digits.startsWith('0') && digits.length === 10) return digits.slice(1);
  return digits;
}

const OWN_WALLET_PAY_MESSAGE = 'You cannot pay your own wallet.';
const DUPLICATE_BANK_ACCOUNT_MESSAGE = 'You already have this bank account. Each account number can only be saved once.';

type EditingAccountOrigin = {
  paymentMethodId: number | null;
  accountType: 'mymoolah' | 'bank';
  identifier: string;
  bankName: string;
  msisdn: string;
};

export function SendMoneyPage() {
  const navigate = useNavigate();
  const { requiresKYC, user } = useAuth();
  
  // Constants to replace magic numbers and hardcoded values
  const CURRENT_USER_ID = user?.id || '1'; // Use actual user ID from context
  const TRANSACTION_ID_LENGTH = 8;
  const TRANSACTION_PREFIX = 'TX';
  const MAX_REFERENCE_LENGTH = 20;
  const DEFAULT_CURRENCY = 'ZAR';
  const DEFAULT_TRANSACTION_LIMIT = 50;
  
  // Helper function to show errors professionally
  const showError = (title: string, message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    setErrorModalData({ title, message, type });
    setShowErrorModal(true);
  };
  
  // Local state management for scalable architecture
  const [allTransactions, setAllTransactions] = useState<FetchedWalletTransaction[]>([]);

  // Fetch transactions from API
  const fetchTransactions = async () => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/wallets/transactions?limit=${DEFAULT_TRANSACTION_LIMIT}`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const sourceList = Array.isArray(data.data)
            ? data.data
            : (data.data.transactions || []);

          const transformedTransactions = collapseDeclinedInstantPayments(sourceList).map((tx: any) => {
            // Backend transforms: credit->deposit, debit->payment, send->sent, receive->received
            // Also treat 'refund' as credit for display
            const isCredit = ['deposit', 'received', 'refund'].includes(tx.type);
            const displayType: FetchedWalletTransaction['type'] = isCredit ? 'received' : 'sent';

            return {
              id: tx.id || `tx_${tx.transactionId}`,
              type: displayType,
              amount: isCredit ? Math.abs(tx.amount) : -Math.abs(tx.amount),
              currency: tx.currency || DEFAULT_CURRENCY,
              description: tx.description || 'Transaction',
              date: new Date(tx.createdAt || tx.date).toLocaleDateString('en-ZA', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }),
              timestamp: new Date(tx.createdAt || tx.date).toISOString(),
              status: (tx.status || 'completed') as FetchedWalletTransaction['status'],
              counterparty: tx.metadata?.counterpartyIdentifier || 'Unknown',
              senderWalletId: tx.senderWalletId || tx.metadata?.senderWalletId,
              receiverWalletId: tx.receiverWalletId || tx.metadata?.receiverWalletId,
              metadata: tx.metadata || {}
            };
          });
          setAllTransactions(transformedTransactions);
        }
      }
    } catch (error) {
      logError('SendMoneyPage', 'Failed to fetch transactions', error as Error);
    }
  };

  // Load transactions on component mount
  useEffect(() => {
    fetchTransactions();
    fetchWalletBalance();
  }, []);
  
  // State Management
  const [beneficiaries, setBeneficiaries] = useState<PaymentBeneficiary[]>([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);

  const loadBeneficiaries = useCallback(async () => {
    try {
      setBeneficiariesLoading(true);
      const backendBeneficiaries = await beneficiaryService.getPaymentBeneficiaries('', 'beneficiary');
      setBeneficiaries(backendBeneficiaries);
    } catch (error) {
      logError('SendMoneyPage', 'Failed to load beneficiaries', error as Error);
    } finally {
      setBeneficiariesLoading(false);
    }
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'alphabetical' | 'lastPaid' | 'favorite'>('lastPaid');
  const [filterType, setFilterType] = useState<'all' | 'mymoolah' | 'bank'>('all');
  const [isOneTimeMode, setIsOneTimeMode] = useState(false);
  
  // API Integration State
  const [recipientInfo, setRecipientInfo] = useState<RecipientInfo | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<RecipientMethod | null>(null);
  const [paymentQuote, setPaymentQuote] = useState<PaymentQuote | null>(null);
  const [isResolvingRecipient, setIsResolvingRecipient] = useState(false);
  const [isGettingQuote, setIsGettingQuote] = useState(false);
  const [isProcessingTransfer, setIsProcessingTransfer] = useState(false);
  const [transferResult, setTransferResult] = useState<TransferResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Add Beneficiary Modal State
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  const [showPayNow, setShowPayNow] = useState(false);
  // Unified post-payment "Save Beneficiary?" prompt (shared SaveContactPrompt component).
  // When set, the prompt is shown. `tempAccountRef` is present only for one-time bank
  // recipients whose account was already created to make the payment: saving keeps it,
  // declining removes it. For MyMoolah recipients we create the beneficiary on save.
  const [contactToSave, setContactToSave] = useState<{
    name: string;
    accountType: 'mymoolah' | 'bank';
    msisdn?: string;
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    tempAccountRef?: { beneficiaryId: string | number; accountId: number } | null;
  } | null>(null);
  const [selectedAccountType, setSelectedAccountType] = useState<'mymoolah' | 'bank' | 'payshap' | 'eft' | 'moolahmove'>('mymoolah');
  const [newBeneficiary, setNewBeneficiary] = useState({
    name: '',
    msisdn: '', // NEW: Mobile number (MSISDN)
    identifier: '',
    bankName: '',
    accountNumber: ''
  });
  const [newMoolahMoveCountry, setNewMoolahMoveCountry] = useState('');
  const [newMoolahMoveChannelId, setNewMoolahMoveChannelId] = useState('');
  const [newMoolahMoveAccountNumber, setNewMoolahMoveAccountNumber] = useState('');
  const newMoolahMoveChannels = newMoolahMoveCountry ? (CHANNELS_BY_COUNTRY[newMoolahMoveCountry] || []) : [];
  const newMoolahMoveChannel = newMoolahMoveChannels.find((channel) => channel.id === newMoolahMoveChannelId);
  const newMoolahMoveSelectedCountry = MOOLAHMOVE_COUNTRIES.find((country) => country.code === newMoolahMoveCountry);
  
  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<PaymentBeneficiary | null>(null);
  // Track selected account ID for beneficiaries with multiple accounts (beneficiaryId -> accountId)
  const [selectedAccountIds, setSelectedAccountIds] = useState<Record<string, number>>({});
  // Track expanded account selectors (beneficiaryId -> boolean)
  const [expandedAccountSelectors, setExpandedAccountSelectors] = useState<Record<string, boolean>>({});
  
  // Confirmation Modal State
  const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
  const [beneficiaryForPayment, setBeneficiaryForPayment] = useState<PaymentBeneficiary | null>(null);
  // Legacy Instant Payment fee dialog state — removed in favour of the
  // universal PaymentProceedModal. (Kept as no-op constants for any stray
  // references during the transition; safe to delete once confirmed unused.)
  const [pendingOneTimeBankAccount, setPendingOneTimeBankAccount] = useState<{
    beneficiaryId: string | number;
    accountId: number;
  } | null>(null);

  // Universal pre-debit proceed confirmation modal. Replaces the legacy
  // instant-fee dialog and gates every outbound wallet debit (P2P, EFT,
  // PayShap instant) behind a single "You are about to pay R{amount} to
  // {recipient}. Proceed?" step. `proceedExecuteRef` holds the flow's
  // submit function and is invoked when the user clicks Yes, proceed.
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [proceedPayload, setProceedPayload] = useState<PaymentProceedPayload | null>(null);
  const proceedExecuteRef = useRef<(() => Promise<void> | void) | null>(null);
  
  // Edit/Remove Beneficiary State
  const [showEditBeneficiaryModal, setShowEditBeneficiaryModal] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<PaymentBeneficiary | null>(null);
  const [editingAccountOrigin, setEditingAccountOrigin] = useState<EditingAccountOrigin | null>(null);
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] = useState(false);
  const [beneficiaryToRemove, setBeneficiaryToRemove] = useState<PaymentBeneficiary | null>(null);

  // Add Account Modal State
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [addAccountTarget, setAddAccountTarget] = useState<PaymentBeneficiary | null>(null);

  // Remove specific account confirmation
  const [showRemoveAccountModal, setShowRemoveAccountModal] = useState(false);
  const [removeAccountTarget, setRemoveAccountTarget] = useState<{
    beneficiary: PaymentBeneficiary;
    accountId: number;
    accountType: 'mymoolah' | 'bank';
    label: string;
  } | null>(null);
  
  // Error Modal State
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState<{
    title: string;
    message: string;
    type: 'error' | 'warning' | 'info';
  }>({
    title: 'Error',
    message: 'An error occurred',
    type: 'error'
  });

  useEffect(() => {
    setNewMoolahMoveChannelId('');
    setNewMoolahMoveAccountNumber('');
  }, [newMoolahMoveCountry]);
  
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [walletBalance, setWalletBalance] = useState<string>('R0.00');
  const [walletBalanceNumeric, setWalletBalanceNumeric] = useState(0);
  const [instantPaymentEnabled, setInstantPaymentEnabled] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | null>(null);
  const [walletBankQuote, setWalletBankQuote] = useState<WalletBankPaymentQuote | null>(null);
  const [recipientIdDraft, setRecipientIdDraft] = useState('');
  const recipientIdCheck = useMemo(
    () => validateRecipientIdentity(recipientIdDraft),
    [recipientIdDraft]
  );
  const quoteNeedsRecipientId = Boolean(
    walletBankQuote?.providerRail === 'ott' && walletBankQuote?.recipientIdentity?.prompt
  );
  const [bankQuoteLoading, setBankQuoteLoading] = useState(false);
  const [showFeeAdjustModal, setShowFeeAdjustModal] = useState(false);
  const [feeAdjustContext, setFeeAdjustContext] = useState<{
    rail: 'eft' | 'payshap';
    feeAmount: number;
    availableBalance: number;
    maxAffordableAmount: number;
    createdAccountRef: { beneficiaryId: string | number; accountId: number } | null;
  } | null>(null);
  const feeAdjustResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const declinedAccountNoticeRef = useRef<number | null>(null);
  // The saved beneficiary that prefilled the Pay Now form (null for a typed one-time
  // recipient). Used so switching MyMoolah <-> Bank Transfer re-derives the correct
  // account (incl. the real bank payment-method id) instead of leaving stale fields.
  const [payNowSourceBeneficiary, setPayNowSourceBeneficiary] = useState<PaymentBeneficiary | null>(null);

  // Fetch wallet balance
  const fetchWalletBalance = async (): Promise<number> => {
    try {
      const token = getToken();
      if (!token) return walletBalanceNumeric;

      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/wallets/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.balance !== undefined) {
          const balance = parseWalletBalanceZar(data.data.available ?? data.data.balance);
          setWalletBalanceNumeric(balance);
          const formattedBalance = balance.toLocaleString('en-ZA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          setWalletBalance(`R${formattedBalance}`);
          return balance;
        }
      }
    } catch (error) {
      logError('SendMoneyPage', 'Failed to fetch wallet balance', error as Error);
    }
    return walletBalanceNumeric;
  };

  const resolveQuoteBankAccountId = useCallback((): number | null => {
    if (selectedBankAccountId) return selectedBankAccountId;
    if (showPaymentModal && selectedBeneficiary?.accountType === 'bank') {
      const accounts = selectedBeneficiary.accounts || [];
      const selectedAccountId = selectedAccountIds[selectedBeneficiary.id];
      if (selectedAccountId) {
        const selectedAccount = accounts.find((a) => a.id === selectedAccountId);
        if (selectedAccount?.type === 'bank') return selectedAccount.id;
      }
      const defaultAccount = accounts.find((a) => a.isDefault) || accounts[0];
      if (defaultAccount?.type === 'bank') return defaultAccount.id;
      const bankAcc = accounts.find((a) => a.type === 'bank');
      return bankAcc?.id ?? null;
    }
    return null;
  }, [selectedBankAccountId, showPaymentModal, selectedBeneficiary, selectedAccountIds]);

  useEffect(() => {
    const amount = parseFloat(paymentAmount);
    const isPayNowBank = showPayNow && (selectedAccountType === 'eft' || selectedAccountType === 'payshap');
    const isSavedBank = showPaymentModal && selectedBeneficiary?.accountType === 'bank';
    const isBankRail = isPayNowBank || isSavedBank;

    if (!isBankRail || !amount || amount <= 0) {
      setWalletBankQuote(null);
      setBankQuoteLoading(false);
      return;
    }

    const rail = instantPaymentEnabled ? 'payshap' : 'eft';
    const bankAccountId = resolveQuoteBankAccountId();
    if (!bankAccountId) {
      setWalletBankQuote(null);
      setBankQuoteLoading(false);
      return;
    }

    let cancelled = false;
    setBankQuoteLoading(true);
    const loadQuote = async () => {
      try {
        const quote = await apiService.quoteWalletBankPayment(bankAccountId, amount, rail);
        if (!cancelled) {
          setWalletBankQuote(quote);
          declinedAccountNoticeRef.current = null;
        }
      } catch (err: any) {
        if (!cancelled) {
          setWalletBankQuote(null);
          if (err?.payload?.error === 'ACCOUNT_PREVIOUSLY_DECLINED' && declinedAccountNoticeRef.current !== bankAccountId) {
            declinedAccountNoticeRef.current = bankAccountId;
            showError('Account declined', err.message, 'warning');
          }
          logError('SendMoneyPage', 'Wallet-bank quote failed', err as Error);
        }
      } finally {
        if (!cancelled) setBankQuoteLoading(false);
      }
    };

    const timer = window.setTimeout(loadQuote, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    paymentAmount,
    selectedAccountType,
    instantPaymentEnabled,
    selectedBankAccountId,
    showPayNow,
    showPaymentModal,
    selectedBeneficiary,
    selectedAccountIds,
    resolveQuoteBankAccountId,
  ]);

  useEffect(() => {
    if (showPayNow || showPaymentModal) {
      void fetchWalletBalance();
    }
  }, [showPayNow, showPaymentModal]);

  // Transform MoolahContext transactions to SendMoneyPage format
  const transformedTransactions = useMemo(() => {
    return allTransactions.map((tx) => {
      // Map MoolahContext types to SendMoneyPage types
      const mapType = (moolahType: string): 'send' | 'receive' | 'deposit' | 'withdraw' | 'transfer' | 'payment' | 'refund' | 'fee' => {
        switch (moolahType) {
          case 'sent': return 'send';
          case 'received': return 'receive';
          case 'payment': return 'payment';
          case 'deposit': return 'deposit';
          default: return 'send';
        }
      };

      const desc = String(tx.description || '');
      const isCreditTx = (tx as any).type === 'received';
      const isDebitTx = (tx as any).type === 'sent' || (tx as any).type === 'payment';

      // Create a user-friendly primary text
      let displayName = desc || 'Transaction';
      
      // Handle voucher transactions
      if (tx.description && tx.description.toLowerCase().includes('voucher')) {
        // Extract voucher number if present
        const voucherMatch = tx.description.match(/(\d{12,16})/);
        if (voucherMatch) {
          const voucherNumber = voucherMatch[1];
          let formattedVoucher: string;
          if (voucherNumber.length === 14) {
            formattedVoucher = voucherNumber.slice(0, 1) + ' ' + 
                              voucherNumber.slice(1, 5) + ' ' + 
                              voucherNumber.slice(5, 9) + ' ' + 
                              voucherNumber.slice(9, 13) + ' ' + 
                              voucherNumber.slice(13);
          } else if (voucherNumber.length === 16) {
            formattedVoucher = voucherNumber.slice(0, 4) + ' ' + 
                              voucherNumber.slice(4, 8) + ' ' + 
                              voucherNumber.slice(8, 12) + ' ' + 
                              voucherNumber.slice(12);
          } else {
            formattedVoucher = voucherNumber.slice(0, 4) + ' ' + 
                              voucherNumber.slice(4, 8) + ' ' + 
                              voucherNumber.slice(8);
          }
          
          if (tx.description.toLowerCase().includes('purchase')) {
            displayName = `Voucher purchase: ${formattedVoucher}`;
          } else if (tx.description.toLowerCase().includes('redemption')) {
            displayName = `Voucher redemption: ${formattedVoucher}`;
          } else {
            displayName = `Voucher transaction: ${formattedVoucher}`;
          }
        } else {
          if (tx.description.toLowerCase().includes('purchase')) {
            displayName = 'Voucher purchase';
          } else if (tx.description.toLowerCase().includes('redemption')) {
            displayName = 'Voucher redemption';
          } else {
            displayName = 'Voucher transaction';
          }
        }
      } else if (isDebitTx) {
        // Parse name and optional reference from description
        const nameMatch = desc.match(/sent to\s+([^|]+)/i) || desc.match(/payment to\s+([^|]+)/i);
        const name = (nameMatch && nameMatch[1]) ? nameMatch[1].trim() : desc.trim();
        const refMatch = desc.match(/ref\s*:\s*([^|]+)/i);
        const ref = refMatch ? refMatch[1].trim().slice(0, 20) : '';
        displayName = name;
      } else if (isCreditTx) {
        // Credit: prefer sender name + optional reference
        const fromMatch = desc.match(/received from\s+([^|]+)/i);
        if (fromMatch && fromMatch[1]) {
          const sender = fromMatch[1].trim();
          const refMatch = desc.match(/ref\s*:\s*([^|]+)/i);
          const ref = refMatch ? refMatch[1].trim().slice(0, 20) : '';
          displayName = sender;
        }
      }
      
      // Preserve original description for icon logic
      const originalDescription = desc || 'Transaction';

      // Infer account type for this transaction row
      // Determine account type using icon classification helpers
      const looksBank = /external bank|bank transfer|absa|nedbank|standard bank|fnb|capitec|investec|discovery bank|african bank|bidvest bank|postbank|paygate|payfast|paypal/i.test(desc);
      const looksWallet = /sent to|payment to|transfer to|received from|\|/i.test(desc) || !!(tx as any).senderWalletId || !!(tx as any).receiverWalletId;
      const txAccountType: 'mymoolah' | 'bank' = looksBank && !looksWallet ? 'bank' : 'mymoolah';

      return {
        id: String(tx.id || Date.now()),
        beneficiaryName: displayName,
        amount: isCreditTx ? Number(tx.amount || 0) : -Math.abs(Number(tx.amount || 0)),
        date: new Date((tx as any).timestamp || Date.now()),
        status: (tx.status || 'completed') as 'completed' | 'pending' | 'failed',
        reference: tx.id || '',
        accountType: txAccountType,
        description: originalDescription, // Keep original for icon logic
        kind: isDebitTx ? ('send' as const) : ('other' as const),
        receiverWalletId: (tx as any).receiverWalletId,
        senderWalletId: (tx as any).senderWalletId,
        type: mapType(tx.type), // Map to SendMoneyPage type
        metadata: {} // Add metadata field
      };
    });
  }, [allTransactions]);

  // Check KYC requirements
  useEffect(() => {
    if (requiresKYC('send')) {
      navigate('/kyc/documents?returnTo=/send-money');
    }
  }, [requiresKYC, navigate]);

  // Initial data load from backend
  useEffect(() => {
    loadBeneficiaries();
  }, [loadBeneficiaries]);

  // Compute last 10 most recent transactions (wallet + bank only; exclude vouchers)
  const lastTenSendTx = useMemo(() => {
    const nonVoucher = transformedTransactions.filter((t) => {
      const d = String(t.description || '').toLowerCase();
      if (d.includes('voucher')) return false;
      if (filterType === 'bank') return t.accountType === 'bank';
      if (filterType === 'mymoolah') return t.accountType === 'mymoolah';
      return true;
    });
    return nonVoucher
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 10);
  }, [transformedTransactions, filterType]);

  // Helper: When clicking a recent row, prefill payment (saved or one-time)
  const prefillPaymentFromTransaction = async (t: Transaction) => {
    // Always drive the one-time Pay Now flow as requested
    const clickedNameRaw = (t.beneficiaryName || '').split(' — Ref:')[0].trim();
    const clickedName = clickedNameRaw.toLowerCase();
    const match = beneficiaries.find(b => (b.name || '').trim().toLowerCase() === clickedName);

    // Prefer the PRECEDING TRANSACTION's account type; fall back to saved, then MyMoolah
    const accountType: 'mymoolah' | 'bank' = (t.accountType as any) || match?.accountType || 'mymoolah';

    let name = match?.name || clickedNameRaw;
    let identifier = '';
    let bankName = '';

    const toDisplaySA = (num: string): string => {
      const digits = String(num || '').replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('27')) return '27' + digits.slice(-9);
      if (digits.startsWith('0')) return '27' + digits.slice(1);
      if (digits.length === 9) return '27' + digits;
      return digits;
    };

    if (accountType === 'mymoolah') {
      // Resolve phone via wallet if possible
      try {
        if (t.receiverWalletId) {
          const wallet = await apiService.getWalletById(t.receiverWalletId);
          const user = wallet?.user;
          if (user?.phoneNumber) identifier = toDisplaySA(String(user.phoneNumber));
          if (!name && (user?.firstName || user?.lastName)) {
            name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
        }
      } catch (_) { /* no-op */ }
      // If still no phone, use saved only if saved is also MyMoolah
      if (!identifier && match?.accountType === 'mymoolah') {
        identifier = toDisplaySA(match.identifier);
      }
    } else {
      // Bank payment: use saved bank details if available
      if (match?.accountType === 'bank') {
        identifier = match.identifier;
        bankName = match.bankName || '';
      }
    }

    // Prefill one-time modal based on chosen account type
    setSelectedAccountType(accountType);
    if (accountType === 'mymoolah') {
      setNewBeneficiary(prev => ({
        ...prev,
        name,
        identifier,
        bankName: '',
        accountNumber: ''
      }));
    } else {
      setNewBeneficiary(prev => ({
        ...prev,
        name,
        identifier, // account number
        bankName,
        accountNumber: identifier
      }));
    }

    // Remember the matched saved beneficiary (if any) so a MyMoolah <-> Bank toggle can
    // re-derive its accounts; null means a typed one-time recipient.
    setPayNowSourceBeneficiary(match || null);

    // Clear amount and reference/description for fresh entry
    setPaymentAmount('');
    setPaymentReference('');
    setPaymentDescription('');

    // Open Pay Now
    setIsOneTimeMode(true);
    setShowPayNow(true);
  };

  // Helper: Get selected account for a beneficiary (or default)
  const getSelectedAccount = (b: PaymentBeneficiary) => {
    if (!b.accounts || b.accounts.length === 0) {
      return null; // No accounts, use legacy format
    }
    const selectedAccountId = selectedAccountIds[b.id];
    if (selectedAccountId) {
      const account = b.accounts.find(a => a.id === selectedAccountId);
      if (account) return account;
    }
    // Return default account or first account
    return b.accounts.find(a => a.isDefault) || b.accounts[0] || null;
  };

  // Helper: Toggle account selector expansion
  const toggleAccountSelector = (beneficiaryId: string) => {
    setExpandedAccountSelectors(prev => ({
      ...prev,
      [beneficiaryId]: !prev[beneficiaryId]
    }));
  };

  // Helper: Select account for a beneficiary
  const selectAccount = (beneficiaryId: string, accountId: number) => {
    setSelectedAccountIds(prev => ({
      ...prev,
      [beneficiaryId]: accountId
    }));
    setExpandedAccountSelectors(prev => ({
      ...prev,
      [beneficiaryId]: false // Collapse after selection
    }));
  };

  // Prefill from a saved beneficiary (Frequent list)
  const prefillFromBeneficiary = (b: PaymentBeneficiary, accountId?: number) => {
    // If accountId provided, use that account; otherwise use selected or default
    const account = accountId 
      ? b.accounts?.find(a => a.id === accountId)
      : getSelectedAccount(b);
    
    // Determine payment rail and details from selected account or legacy format
    // Bank recipients can later use standard EFT or instant rail at payment time.
    let rawAccountType: 'mymoolah' | 'bank' = b.accountType;
    let accountNumber = ''; // bank account number (identifier in BeneficiaryPaymentMethod)
    let bankName = b.bankName;
    // Real contact phone for FICA / MyMoolah lookup. resolveContactMobile guarantees
    // we never use the bank account number (b.identifier / account.identifier) here.
    const msisdn = resolveContactMobile(b, account);

    if (account) {
      rawAccountType = account.type === 'bank' ? 'bank' : 'mymoolah';
      accountNumber = account.identifier || ''; // bank account number from normalized table
      bankName = account.metadata?.bankName || b.bankName;
      // msisdn stays as the beneficiary's phone — NOT the account number
    }

    // Bank payments default to H2H EFT; Instant Payment is selected in the payment modal.
    const paymentRail = rawAccountType === 'bank' ? 'eft' : 'mymoolah';
    setSelectedAccountType(paymentRail as any);
    setInstantPaymentEnabled(false);
    setSelectedBankAccountId(rawAccountType === 'bank' && account?.id ? account.id : null);

    const toDisplaySA = (num: string): string => {
      const digits = String(num || '').replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('27')) return '27' + digits.slice(-9);
      if (digits.startsWith('0')) return '27' + digits.slice(1);
      if (digits.length === 9) return '27' + digits;
      return digits;
    };

    if (rawAccountType === 'mymoolah') {
      setNewBeneficiary(prev => ({
        ...prev,
        name: b.name,
        identifier: toDisplaySA(msisdn),
        msisdn: toDisplaySA(msisdn),
        bankName: '',
        accountNumber: ''
      }));
    } else {
      // Bank transfer: identifier = bank account number, msisdn = beneficiary phone for FICA
      setNewBeneficiary(prev => ({
        ...prev,
        name: b.name,
        identifier: accountNumber,      // bank account number
        msisdn: toDisplaySA(msisdn),    // phone number (FICA / MSISDN)
        bankName: bankName || '',
        accountNumber: accountNumber
      }));
    }
    setPayNowSourceBeneficiary(b);
    setPaymentAmount('');
    setPaymentReference('');
    setPaymentDescription('');
    setIsOneTimeMode(true);
    setShowPayNow(true);
  };

  // Toggle the Pay Now payment method (MyMoolah <-> Bank Transfer). When the form was
  // prefilled from a saved beneficiary, re-derive that contact's matching account so the
  // bank name / account number AND the real bank payment-method id are restored (a saved
  // beneficiary with both a wallet and a bank account must resolve to the bank pm id, not
  // the wallet/MSISDN, or the wallet->bank quote 404s). For a typed one-time recipient,
  // switch the rail and clear the wallet-number value so the user can enter bank details.
  const applyPayNowMethod = (method: 'mymoolah' | 'bank') => {
    setInstantPaymentEnabled(false);
    const toDisplaySA = (num: string): string => {
      const digits = String(num || '').replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('27')) return '27' + digits.slice(-9);
      if (digits.startsWith('0')) return '27' + digits.slice(1);
      if (digits.length === 9) return '27' + digits;
      return digits;
    };
    const source = payNowSourceBeneficiary;

    if (method === 'mymoolah') {
      if (selectedAccountType === 'mymoolah') return;
      setSelectedAccountType('mymoolah');
      setSelectedBankAccountId(null);
      const walletAcc = source?.accounts?.find(a => a.type !== 'bank') || null;
      const msisdn = source ? resolveContactMobile(source, walletAcc || undefined) : '';
      setNewBeneficiary(prev => ({
        ...prev,
        name: source?.name ?? prev.name,
        identifier: toDisplaySA(msisdn),
        msisdn: toDisplaySA(msisdn),
        bankName: '',
        accountNumber: '',
      }));
      return;
    }

    // method === 'bank'
    const alreadyBank = selectedAccountType === 'eft' || selectedAccountType === 'payshap';
    setSelectedAccountType('eft');
    if (alreadyBank) return; // already on a bank rail — keep whatever is entered/selected
    const bankAcc = source?.accounts?.find(a => a.type === 'bank') || null;
    if (bankAcc) {
      const acctNum = (bankAcc as any).accountNumber || bankAcc.identifier || '';
      const bankName = (bankAcc as any).metadata?.bankName || source?.bankName || '';
      const msisdn = source ? resolveContactMobile(source, bankAcc) : '';
      setSelectedBankAccountId(bankAcc.id || null);
      setNewBeneficiary(prev => ({
        ...prev,
        name: source?.name ?? prev.name,
        identifier: acctNum,
        accountNumber: acctNum,
        bankName,
        msisdn: toDisplaySA(msisdn) || prev.msisdn,
      }));
      return;
    }
    // No saved bank account: manual entry. Clear the wallet-number value left over from
    // the MyMoolah view so it is not mistaken for a bank account number.
    setSelectedBankAccountId(null);
    setNewBeneficiary(prev => ({
      ...prev,
      identifier: '',
      accountNumber: '',
      bankName: '',
    }));
  };

  // API Functions
  const resolveRecipient = async (identifier: string) => {
    try {
      setIsResolvingRecipient(true);
      setError(null);
      setRecipientInfo(null);
      setSelectedPaymentMethod(null);
      setPaymentQuote(null);

      const info = await apiService.resolveRecipient(identifier);
      setRecipientInfo(info);
      
      // Auto-select preferred method if available
      const preferredMethod = info.availableMethods.find(method => method.preferred);
      if (preferredMethod) {
        setSelectedPaymentMethod(preferredMethod);
      }
      
          } catch (err) {
        logError('SendMoneyPage', 'Failed to resolve recipient', err as Error);
        setError(err instanceof Error ? err.message : 'Failed to resolve recipient');
      } finally {
      setIsResolvingRecipient(false);
    }
  };

  const getPaymentQuote = async (paymentMethodId: string, amount: number, recipient: string) => {
    try {
      setIsGettingQuote(true);
      setError(null);

      const quote = await apiService.getPaymentQuote(paymentMethodId, amount, recipient);
      setPaymentQuote(quote);
      
          } catch (err) {
        logError('SendMoneyPage', 'Failed to get payment quote', err as Error);
        setError(err instanceof Error ? err.message : 'Failed to get payment quote');
      } finally {
      setIsGettingQuote(false);
    }
  };

  const processTransfer = async (paymentMethodId: string, amount: number, recipient: string, reference: string) => {
    try {
      setIsProcessingTransfer(true);
      setError(null);

      const result = await apiService.initiateTransfer(paymentMethodId, amount, recipient, reference);
      setTransferResult(result);
      
      // Close payment modal on success
      setShowPaymentModal(false);
      
      // Show success message or redirect
      if (result.status === 'processing') {
        // Could show a success modal or redirect to status page

      }
      
          } catch (err) {
        logError('SendMoneyPage', 'Failed to process transfer', err as Error);
        setError(err instanceof Error ? err.message : 'Failed to process transfer');
      } finally {
      setIsProcessingTransfer(false);
    }
  };

  // Filtered and Sorted Beneficiaries
  const filteredBeneficiaries = useMemo(() => {
    const filtered = beneficiaries.filter(beneficiary => {
      const matchesSearch = beneficiary.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           beneficiary.identifier.includes(searchQuery) ||
                           (beneficiary.msisdn && beneficiary.msisdn.includes(searchQuery)); // NEW: Search by MSISDN
      
      const matchesFilter = filterType === 'all' || beneficiary.accountType === filterType;
      
      return matchesSearch && matchesFilter;
    });

    // Sort beneficiaries
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        case 'lastPaid': {
          // Prioritize beneficiaries by most recent activity
          // First: beneficiaries with recent payments (most recent first)
          // Second: beneficiaries with older payments
          // Third: beneficiaries never paid (alphabetical)
          if (!a.lastPaid && !b.lastPaid) {
            // Both never paid - sort alphabetically
            return a.name.localeCompare(b.name);
          }
          if (!a.lastPaid) return 1; // a never paid, b has payments
          if (!b.lastPaid) return -1; // b never paid, a has payments

          // Both have payments - sort by most recent first
          const timeDiff = b.lastPaid.getTime() - a.lastPaid.getTime();
          if (timeDiff !== 0) return timeDiff;

          // If same last payment date, prioritize by payment count (most frequent first)
          return (b.paymentCount || 0) - (a.paymentCount || 0);
        }
        case 'favorite':
          if (a.isFavorite === b.isFavorite) {
            return a.name.localeCompare(b.name);
          }
          return a.isFavorite ? -1 : 1;
        default:
          return 0;
      }
    });

    return filtered;
  }, [beneficiaries, searchQuery, sortBy, filterType]);

  const payNowWalletNumber = newBeneficiary.msisdn || newBeneficiary.identifier || '';
  const isOwnWalletPay = selectedAccountType === 'mymoolah'
    && msisdnEquals(payNowWalletNumber, user?.phoneNumber);
  const savedBankWithSameAccount = findContactByBankAccount(beneficiaries, newBeneficiary.identifier);
  const payingExistingSavedBank = !!selectedBankAccountId && (savedBankWithSameAccount?.accounts || []).some((account) => (
    account.id === selectedBankAccountId
    && digitsOnly(account.accountNumber || account.identifier) === digitsOnly(newBeneficiary.identifier)
  ));
  const isDuplicateNewBankAccount = (
    selectedAccountType === 'bank' || selectedAccountType === 'eft' || selectedAccountType === 'payshap'
  ) && !!digitsOnly(newBeneficiary.identifier) && !!savedBankWithSameAccount && !payingExistingSavedBank;

  // Handle Add Beneficiary
  const handleAddBeneficiary = async () => {
    if (selectedAccountType === 'moolahmove') {
      if (!MOOLAHMOVE_ENABLED) {
        showError('MoolahMove', 'MoolahMove is not available in this environment yet.', 'warning');
        return;
      }
      if (!newBeneficiary.name.trim()) {
        showError('Validation Error', 'Please enter the beneficiary name.', 'warning');
        return;
      }
      if (!newMoolahMoveCountry) {
        showError('Validation Error', 'Please select the destination country.', 'warning');
        return;
      }
      if (!newMoolahMoveChannelId || !newMoolahMoveChannel) {
        showError('Validation Error', 'Please select the MoolahMove payment method.', 'warning');
        return;
      }
      if (!newMoolahMoveAccountNumber.trim()) {
        showError(
          'Validation Error',
          newMoolahMoveChannel.type === 'mobile_money' ? 'Please enter the recipient mobile money number.' : 'Please enter the recipient account number.',
          'warning'
        );
        return;
      }

      try {
        const serviceType = newMoolahMoveChannel.type === 'mobile_money' ? 'mobile_money' : 'international_bank';
        await beneficiaryService.createOrUpdateBeneficiary({
          name: newBeneficiary.name.trim(),
          serviceType,
          serviceData: {
            channelId: newMoolahMoveChannelId,
            country: newMoolahMoveCountry,
            countryCode: newMoolahMoveCountry,
            currency: newMoolahMoveSelectedCountry?.currency || '',
            paymentRail: 'moolahmove',
            paymentMethod: newMoolahMoveChannel.type,
            provider: newMoolahMoveChannel.name,
            accountNumber: newMoolahMoveChannel.type === 'mobile_money' ? undefined : newMoolahMoveAccountNumber.trim(),
            mobileMoneyId: newMoolahMoveChannel.type === 'mobile_money' ? newMoolahMoveAccountNumber.trim() : undefined,
            msisdn: newMoolahMoveChannel.type === 'mobile_money' ? newMoolahMoveAccountNumber.trim() : undefined,
            accountName: newBeneficiary.name.trim(),
            isDefault: true
          }
        });
        await loadBeneficiaries();
        setNewBeneficiary({ name: '', msisdn: '', identifier: '', bankName: '', accountNumber: '' });
        setNewMoolahMoveCountry('');
        setNewMoolahMoveChannelId('');
        setNewMoolahMoveAccountNumber('');
        setSelectedAccountType('mymoolah');
        setShowAddBeneficiary(false);
        showError('Success', `${newBeneficiary.name.trim()} added with a MoolahMove account.`, 'info');
      } catch (error: any) {
        logError('SendMoneyPage', 'Failed to add MoolahMove beneficiary', error as Error);
        showError('Error', error?.message || 'Failed to add MoolahMove recipient. Please try again.', 'error');
      }
      return;
    }

    const isBankBeneficiary = selectedAccountType === 'bank' || selectedAccountType === 'eft' || selectedAccountType === 'payshap';
    // Validate required fields including MSISDN
    if (!newBeneficiary.name || !newBeneficiary.msisdn) {
      showError('Validation Error', 'Please fill in all required fields: Name and Mobile Number (MSISDN)', 'warning');
      return;
    }

    // For bank accounts, also require bank name and account number
    if (isBankBeneficiary && (!newBeneficiary.bankName || !newBeneficiary.identifier)) {
      showError('Validation Error', 'For bank payments, please also select a bank and enter the account number', 'warning');
      return;
    }

    // Validate mobile number format using the same validation as registration/login
    if (!validateMobileNumber(newBeneficiary.msisdn)) {
      showError('Validation Error', 'Invalid South African mobile number format. Please enter a valid mobile number (e.g., 078 123 4567)', 'warning');
      return;
    }

    if (!isBankBeneficiary && isOwnWalletPay) {
      showError('You cannot pay your own wallet', OWN_WALLET_PAY_MESSAGE, 'error');
      return;
    }

    if (isBankBeneficiary && isDuplicateNewBankAccount) {
      showError('Bank account already saved', DUPLICATE_BANK_ACCOUNT_MESSAGE, 'error');
      return;
    }

    const walletDestCopy = walletDestinationModalCopy(newBeneficiary.msisdn)
      || walletDestinationModalCopy(newBeneficiary.identifier);
    if (!isBankBeneficiary && walletDestCopy) {
      showError(walletDestCopy.title, walletDestCopy.message, 'error');
      return;
    }

    if (isBankBeneficiary) {
      const acctCheck = validateSaBankAccountNumber(newBeneficiary.identifier, { bankName: newBeneficiary.bankName });
      if (!acctCheck.valid) {
        const copy = bankFieldModalCopy(acctCheck)!;
        showError(copy.title, copy.message, 'error');
        return;
      }
    }

    try {
      const legacyAccountType: 'mymoolah' | 'bank' = isBankBeneficiary ? 'bank' : 'mymoolah';
      const selectedBank = SA_BANKS.find(bank => bank.name === newBeneficiary.bankName);
      const createdBeneficiary = await beneficiaryService.createPaymentBeneficiary({
        name: newBeneficiary.name.trim(),
        msisdn: newBeneficiary.msisdn.trim(),
        accountType: legacyAccountType,
        bankName: isBankBeneficiary ? newBeneficiary.bankName?.trim() : undefined,
        accountNumber: isBankBeneficiary ? newBeneficiary.identifier.trim() : undefined,
        branchCode: isBankBeneficiary ? selectedBank?.branchCode : undefined,
        role: 'beneficiary'
      });

      await loadBeneficiaries();
      let forPayment = createdBeneficiary;
      try {
        const list = await beneficiaryService.getPaymentBeneficiaries('', 'beneficiary');
        const fresh = list.find((b) => String(b.id) === String(createdBeneficiary.id));
        if (fresh?.accounts?.some((a) => a.type === 'bank' && a.id)) {
          forPayment = fresh;
        }
      } catch (refreshErr) {
        logError('SendMoneyPage', 'Failed to refresh beneficiary after create', refreshErr as Error);
      }

      setNewBeneficiary({ name: '', msisdn: '', identifier: '', bankName: '', accountNumber: '' });
      setShowAddBeneficiary(false);

      // Show confirmation modal asking if user wants to make payment now
      setBeneficiaryForPayment(forPayment);
      setShowPaymentConfirmationModal(true);
    } catch (error: any) {
      logError('SendMoneyPage', 'Failed to add beneficiary', error as Error);
      showError('Error', error?.message || 'Failed to add recipient. Please try again.', 'error');
    }
  };

  // Account the pencil form edits: the one selected on the card, otherwise
  // the default bank account, otherwise the first bank account. Wallet cards
  // use the selected or default MyMoolah account.
  const resolveAccountForEdit = (beneficiary: PaymentBeneficiary): BeneficiaryAccount | null => {
    const paymentAccounts = (beneficiary.accounts || []).filter(
      (account) => account.type === 'bank' || account.type === 'mymoolah'
    );
    const selectedId = selectedAccountIds[beneficiary.id];
    if (selectedId) {
      const selected = paymentAccounts.find((account) => account.id === selectedId);
      if (selected) return selected;
    }
    if (beneficiary.accountType === 'bank') {
      const banks = paymentAccounts.filter((account) => account.type === 'bank');
      return banks.find((account) => account.isDefault) || banks[0] || null;
    }
    const wallets = paymentAccounts.filter((account) => account.type === 'mymoolah');
    return wallets.find((account) => account.isDefault) || wallets[0] || null;
  };

  // Handle Edit Beneficiary
  const handleEditBeneficiary = (beneficiary: PaymentBeneficiary) => {
    // For bank beneficiaries, populate identifier from the normalized accounts[] array
    // (the legacy beneficiary.identifier holds the phone/NON_MSI_ value, not the account number)
    const account = resolveAccountForEdit(beneficiary);
    let editCopy: PaymentBeneficiary = { ...beneficiary };
    let origin: EditingAccountOrigin = {
      paymentMethodId: null,
      accountType: beneficiary.accountType === 'bank' ? 'bank' : 'mymoolah',
      identifier: '',
      bankName: beneficiary.bankName || '',
      msisdn: comparableMsisdn(beneficiary.msisdn),
    };

    if (account?.type === 'bank') {
      const accountNumber = digitsOnly(account.accountNumber || account.identifier);
      const bankName = account.bankName || account.metadata?.bankName || beneficiary.bankName || '';
      editCopy = {
        ...editCopy,
        accountType: 'bank',
        identifier: accountNumber,
        bankName,
      };
      origin = {
        paymentMethodId: Number.isInteger(account.id) ? account.id : null,
        accountType: 'bank',
        identifier: accountNumber,
        bankName,
        msisdn: origin.msisdn,
      };
    } else if (account?.type === 'mymoolah') {
      const msisdn = account.walletMsisdn || account.identifier || beneficiary.msisdn || '';
      editCopy = {
        ...editCopy,
        accountType: 'mymoolah',
        msisdn,
        identifier: account.identifier || beneficiary.identifier,
      };
      origin = {
        paymentMethodId: Number.isInteger(account.id) ? account.id : null,
        accountType: 'mymoolah',
        identifier: '',
        bankName: '',
        msisdn: comparableMsisdn(msisdn),
      };
    } else if (beneficiary.accountType === 'bank') {
      const bankAccount = beneficiary.accounts?.find(a => a.type === 'bank') || beneficiary.accounts?.[0];
      if (bankAccount) {
        const accountNumber = digitsOnly(bankAccount.accountNumber || bankAccount.identifier);
        const bankName = bankAccount.bankName || bankAccount.metadata?.bankName || beneficiary.bankName || '';
        editCopy = {
          ...editCopy,
          identifier: accountNumber,
          bankName,
        };
        origin = {
          ...origin,
          paymentMethodId: Number.isInteger(bankAccount.id) ? bankAccount.id : null,
          identifier: accountNumber,
          bankName,
        };
      }
    }
    setEditingAccountOrigin(origin);
    setEditingBeneficiary(editCopy);
    setShowEditBeneficiaryModal(true);
  };

  // Handle Remove Beneficiary
  const handleRemoveBeneficiary = (beneficiary: PaymentBeneficiary) => {
    setBeneficiaryToRemove(beneficiary);
    setShowRemoveConfirmationModal(true);
  };

  // Handle Confirm Remove Beneficiary
  const handleConfirmRemoveBeneficiary = async () => {
    if (!beneficiaryToRemove) return;
    
    try {
      // Backend removal (payment context) only if id is numeric; skip backend for local-only temp ids
      const numericId = Number(beneficiaryToRemove.id);
      const isNumericId = Number.isInteger(numericId) && !Number.isNaN(numericId);
      if (isNumericId) {
        await beneficiaryService.removeBeneficiary(beneficiaryToRemove.id as any, 'payment');
      } else {
        console.warn('Skipping backend removal for non-numeric beneficiary id', beneficiaryToRemove.id);
      }

      // Remove from local state
      setBeneficiaries(prev => prev.filter(b => b.id !== beneficiaryToRemove.id));
      
      // Clear selection if this was the selected beneficiary
      if (selectedBeneficiary?.id === beneficiaryToRemove.id) {
        setSelectedBeneficiary(null);
      }
      
      setBeneficiaryToRemove(null);
      setShowRemoveConfirmationModal(false);
    } catch (error) {
      logError('SendMoneyPage', 'Failed to remove beneficiary', error as Error);
      showError('Error', 'Failed to remove recipient. Please try again.', 'error');
    }
  };

  // Handle removing a specific account from a beneficiary
  const handleConfirmRemoveAccount = async () => {
    if (!removeAccountTarget) return;
    const { beneficiary, accountId, accountType } = removeAccountTarget;
    try {
      await beneficiaryService.removePaymentAccount(beneficiary.id, accountType, accountId);
      // Refresh beneficiary list
      await loadBeneficiaries();
      setRemoveAccountTarget(null);
      setShowRemoveAccountModal(false);
    } catch (err: any) {
      logError('SendMoneyPage', 'Failed to remove account', err as Error);
      showError('Error', err?.message || 'Failed to remove account. Please try again.', 'error');
    }
  };

  const getWalletBankPaymentErrorMessage = (err: ApiError | Error | any) => {
    const payload = err?.payload || {};
    const details = payload.details || {};
    if (payload.error === 'INSUFFICIENT_AVAILABLE_BALANCE' || err?.code === 'INSUFFICIENT_AVAILABLE_BALANCE') {
      return 'Insufficient available balance for this payment (including fees and pending payments).';
    }
    if (payload.error === 'INSUFFICIENT_BALANCE' && details.maximumPaymentAmount !== undefined) {
      const instantPaymentFee = Number(details.instantPaymentFee || 0);
      const totalUserFee = Number(details.totalUserFee || instantPaymentFee);
      const maximumPaymentAmount = Number(details.maximumPaymentAmount || 0);
      const feeMessage = totalUserFee > instantPaymentFee
        ? `Instant Payment bank fee is ${formatCurrency(instantPaymentFee)} and total fee is ${formatCurrency(totalUserFee)}.`
        : `Instant Payment fee is ${formatCurrency(instantPaymentFee)}.`;
      return `${feeMessage} You can send up to ${formatCurrency(maximumPaymentAmount)} with your current balance.`;
    }
    if (payload.error === 'BANK_ACCOUNT_IS_MOBILE' || err?.code === 'BANK_ACCOUNT_IS_MOBILE') {
      return err?.message || payload.message || 'This looks like a cellphone number, not a bank account number.';
    }
    if (payload.error === 'MOBILE_IS_BANK_ACCOUNT' || err?.code === 'MOBILE_IS_BANK_ACCOUNT') {
      return err?.message || payload.message || 'This looks like a bank account number, not a cellphone.';
    }
    if (payload.error === 'INSTANT_PAYMENT_CARD_PAN' || err?.code === 'INSTANT_PAYMENT_CARD_PAN') {
      return err?.message || payload.message || 'This looks like a card number, not a bank account number.';
    }
    if (payload.error === 'ACCOUNT_PREVIOUSLY_DECLINED' || err?.code === 'ACCOUNT_PREVIOUSLY_DECLINED') {
      return err?.message || payload.message || 'This account was declined by the bank. Update the account number or branch before sending again.';
    }
    return err?.message || 'Bank payment failed. Please try again.';
  };

  const presentPaymentOutcomeModal = async (paymentId: string) => {
    try {
      const status = await pollEftPaymentStatus(paymentId);
      await fetchWalletBalance();
      await fetchTransactions();
      const copy = eftStatusModalCopy(status);
      showError(copy.title, copy.message, copy.type);
    } catch (pollErr: any) {
      showError(
        'Payment submitted',
        'Your payment was submitted. We could not confirm the final status yet — check your balance and transaction history.',
        'info'
      );
    }
  };

  const presentEftOutcomeModal = presentPaymentOutcomeModal;

  const createPayNowBankAccount = async () => {
    const selectedBank = SA_BANKS.find(bank => bank.name === newBeneficiary.bankName);
    const enteredAccountNumber = newBeneficiary.identifier.trim();
    const acctCheck = validateSaBankAccountNumber(enteredAccountNumber, { bankName: newBeneficiary.bankName });
    if (!acctCheck.valid) {
      throw new Error(acctCheck.error || 'Invalid bank account number');
    }
    const digitsOnly = (v?: string) => String(v || '').replace(/\D/g, '');
    const createdBeneficiary = await beneficiaryService.createPaymentBeneficiary({
      name: newBeneficiary.name.trim(),
      msisdn: newBeneficiary.msisdn.trim(),
      accountType: 'bank',
      bankName: newBeneficiary.bankName?.trim(),
      accountNumber: acctCheck.normalized,
      branchCode: selectedBank?.branchCode,
      role: 'beneficiary',
    });

    // Resolve the REAL bank payment-method id from the authoritative normalized
    // accounts. NEVER fall back to a non-bank account: a beneficiary with both a
    // MyMoolah wallet and a bank account must not resolve to the wallet/MSISDN id,
    // which caused wallet-to-bank EFT/PayShap to fail with "Bank beneficiary
    // account not found".
    const findBankAccount = (accounts?: Array<{ id: number; type: string; identifier?: string; accountNumber?: string }>) => {
      const bankAccounts = (accounts || []).filter(a => a.type === 'bank');
      return (
        bankAccounts.find(a => digitsOnly(a.accountNumber || a.identifier) === digitsOnly(enteredAccountNumber)) ||
        bankAccounts[0] ||
        null
      );
    };

    let bankAccount = findBankAccount(createdBeneficiary.accounts as any);
    try {
      // The create response can carry a synthetic account id; the GET services
      // endpoint returns the authoritative normalized payment-method id.
      const authoritative = await beneficiaryService.getBeneficiaryServices(Number(createdBeneficiary.id));
      const resolved = findBankAccount(authoritative.accounts as any);
      if (resolved?.id) bankAccount = resolved;
    } catch (err) {
      logError('SendMoneyPage', 'Failed to refresh bank account after create', err as Error);
    }

    const bankAccountId = bankAccount?.id || null;
    if (!bankAccountId) {
      throw new Error('Bank beneficiary account could not be created');
    }

    const accountRef = {
      beneficiaryId: createdBeneficiary.id,
      accountId: bankAccountId,
    };
    setSelectedBankAccountId(bankAccountId);
    setPendingOneTimeBankAccount(accountRef);
    return accountRef;
  };

  const removeOneTimeBankAccount = async (accountRef: { beneficiaryId: string | number; accountId: number } | null) => {
    if (!accountRef) return;
    try {
      await beneficiaryService.removePaymentAccount(accountRef.beneficiaryId, 'bank', accountRef.accountId);
    } catch (err) {
      logError('SendMoneyPage', 'Failed to remove one-time bank recipient after Pay Now', err as Error);
    }
  };

  // Cancel the universal proceed modal. If a throwaway one-time bank account
  // was created to quote a PayShap instant payment, remove it so a cancelled
  // confirm never leaves an orphan recipient on the backend.
  const cancelProceed = () => {
    if (pendingOneTimeBankAccount) {
      void removeOneTimeBankAccount(pendingOneTimeBankAccount);
      setPendingOneTimeBankAccount(null);
      setSelectedBankAccountId(null);
    }
    setWalletBankQuote(null);
    setRecipientIdDraft('');
    setProceedPayload(null);
    proceedExecuteRef.current = null;
    setShowProceedModal(false);
  };

  // Open the universal proceed modal with a payload + an execute callback
  // that runs the existing submit handler when the user clicks Yes.
  const openProceed = (payload: PaymentProceedPayload, execute: () => Promise<void> | void) => {
    proceedExecuteRef.current = execute;
    setProceedPayload(payload);
    setShowProceedModal(true);
  };

  // Proceed clicked — invoke the flow's execute function. The execute
  // function is responsible for setting isProcessing, closing its parent
  // dialog on success, and calling closeProceedModal() on success.
  const handleProceed = async () => {
    const fn = proceedExecuteRef.current;
    if (!fn) {
      setShowProceedModal(false);
      return;
    }
    try {
      await fn();
    } catch (err) {
      logError('SendMoneyPage', 'Proceed execute threw', err as Error);
    }
  };

  // Close the proceed modal after a successful submit. Does NOT remove the
  // one-time account (the success path already handled persistence / save prompt).
  const closeProceedModal = () => {
    setProceedPayload(null);
    proceedExecuteRef.current = null;
    setShowProceedModal(false);
    setRecipientIdDraft('');
  };

  const promptFeeAdjust = (
    ctx: {
      rail: 'eft' | 'payshap';
      feeAmount: number;
      availableBalance: number;
      maxAffordableAmount: number;
      createdAccountRef: { beneficiaryId: string | number; accountId: number } | null;
    },
  ): Promise<boolean> => new Promise((resolve) => {
    feeAdjustResolverRef.current = resolve;
    setFeeAdjustContext(ctx);
    setShowFeeAdjustModal(true);
  });

  const handleFeeAdjustConfirm = () => {
    const resolver = feeAdjustResolverRef.current;
    feeAdjustResolverRef.current = null;
    setShowFeeAdjustModal(false);
    setFeeAdjustContext(null);
    if (resolver) resolver(true);
  };

  const handleFeeAdjustCancel = async () => {
    const resolver = feeAdjustResolverRef.current;
    feeAdjustResolverRef.current = null;
    setShowFeeAdjustModal(false);
    if (feeAdjustContext?.createdAccountRef) {
      await removeOneTimeBankAccount(feeAdjustContext.createdAccountRef);
      setPendingOneTimeBankAccount(null);
      setSelectedBankAccountId(null);
    }
    setFeeAdjustContext(null);
    if (resolver) resolver(false);
  };

  const buildBankProceedPayload = (
    flow: 'payNow' | 'savedBeneficiary',
    amount: number,
    quote: WalletBankPaymentQuote,
    rail: 'eft' | 'payshap',
  ): PaymentProceedPayload => {
    const recipientName = flow === 'payNow'
      ? newBeneficiary.name
      : (selectedBeneficiary?.name || 'this account');
    const last4 = quote?.beneficiary?.accountNumberLast4;
    const verified = quote?.accountResolution;
    const verifiedName = (verified?.resolved && (verified.publicName || verified.legalName))
      ? {
          publicName: verified.publicName || undefined,
          legalName: verified.legalName || undefined,
        }
      : undefined;
    const notices = rail === 'payshap' ? buildInstantPaymentNotices(verified) : undefined;

    return {
      amountCents: randsToCents(amount),
      recipientLabel: recipientName,
      recipientSubLabel: last4 ? `Account ••••${last4}` : undefined,
      contextLabel: rail === 'payshap' ? 'PayShap real-time payment' : 'Bank EFT (1–2 business days)',
      feeAmountCents: randsToCents(Number(quote.feeAmount)),
      totalDebitCents: randsToCents(Number(quote.totalDebit)),
      verifiedName: rail === 'payshap' ? verifiedName : undefined,
      notices,
      title: rail === 'payshap' ? 'Confirm Instant Payment' : 'Confirm Bank EFT',
      proceedButtonText: 'Yes, proceed',
    };
  };

  const prepareBankRailPayment = async (opts: {
    flow: 'payNow' | 'savedBeneficiary';
    amount: number;
    rail: 'eft' | 'payshap';
    bankAccountId: number | null;
    onProceed: () => Promise<void>;
  }) => {
    const { flow, rail, onProceed } = opts;
    let amount = opts.amount;
    let bankAccountId = opts.bankAccountId;
    let createdAccountRef: { beneficiaryId: string | number; accountId: number } | null = null;

    setIsProcessing(true);
    try {
      if (flow === 'payNow' || flow === 'savedBeneficiary') {
        const localAccountNumber = flow === 'payNow'
          ? newBeneficiary.identifier
          : (
            getSelectedAccount(selectedBeneficiary!)?.identifier
            || getSelectedAccount(selectedBeneficiary!)?.accountNumber
            || selectedBeneficiary?.identifier
            || ''
          );
        const localBankName = flow === 'payNow'
          ? newBeneficiary.bankName
          : (
            getSelectedAccount(selectedBeneficiary!)?.metadata?.bankName
            || selectedBeneficiary?.bankName
            || undefined
          );
        const acctCheck = validateSaBankAccountNumber(localAccountNumber, { bankName: localBankName });
        if (!acctCheck.valid) {
          const copy = bankFieldModalCopy(acctCheck)!;
          showError(copy.title, copy.message, 'error');
          return;
        }
        if (rail === 'payshap') {
          assertInstantPaymentAccountNotCardPan(localAccountNumber);
        }
      }

      const availableBalance = await fetchWalletBalance();

      if (!bankAccountId && flow === 'payNow') {
        createdAccountRef = await createPayNowBankAccount();
        bankAccountId = createdAccountRef.accountId;
        setPendingOneTimeBankAccount(createdAccountRef);
        setSelectedBankAccountId(bankAccountId);
      }

      if (!bankAccountId) {
        showError('Validation Error', 'Please select a bank account for this payment', 'warning');
        return;
      }

      const quoteAndOpenProceed = async (quoteAmount: number): Promise<void> => {
        const quote = await apiService.quoteWalletBankPayment(bankAccountId!, quoteAmount, rail);
        const isValidQuote = !!quote
          && Number.isFinite(Number(quote.feeAmount)) && Number(quote.feeAmount) > 0
          && Number.isFinite(Number(quote.totalDebit)) && Number(quote.totalDebit) > 0;

        if (!isValidQuote) {
          throw new Error(rail === 'payshap'
            ? 'Could not load the Instant Payment fee. Please try again.'
            : 'Could not load the bank transfer fee. Please try again.');
        }

        if (isOverAffordableLimit(quoteAmount, Number(quote.totalDebit), availableBalance)) {
          const maxAffordableAmount = computeMaxAffordableAmount(availableBalance, Number(quote.feeAmount));
          if (!canProceedWithBankPayment(maxAffordableAmount)) {
            if (createdAccountRef) {
              await removeOneTimeBankAccount(createdAccountRef);
              setPendingOneTimeBankAccount(null);
              setSelectedBankAccountId(null);
            }
            showError(
              'Insufficient balance',
              'Your balance is too low to cover the transaction fee. Please top up your wallet.',
              'warning',
            );
            return;
          }

          const confirmed = await (async () => {
            setIsProcessing(false);
            const result = await promptFeeAdjust({
              rail,
              feeAmount: Number(quote.feeAmount),
              availableBalance,
              maxAffordableAmount,
              createdAccountRef,
            });
            setIsProcessing(true);
            return result;
          })();

          if (!confirmed) {
            if (createdAccountRef) {
              await removeOneTimeBankAccount(createdAccountRef);
              setPendingOneTimeBankAccount(null);
              setSelectedBankAccountId(null);
            }
            return;
          }

          amount = maxAffordableAmount;
          setPaymentAmount(maxAffordableAmount.toFixed(2));
          await quoteAndOpenProceed(amount);
          return;
        }

        setWalletBankQuote(quote);
        setRecipientIdDraft('');
        const payload = buildBankProceedPayload(flow, quoteAmount, quote, rail);
        openProceed(payload, onProceed);
      };

      await quoteAndOpenProceed(amount);
    } catch (err: any) {
      if (createdAccountRef) {
        await removeOneTimeBankAccount(createdAccountRef);
        setPendingOneTimeBankAccount(null);
        setSelectedBankAccountId(null);
      }
      logError('SendMoneyPage', 'Failed to prepare bank payment', err as Error);
      if (err?.payload?.error === 'ACCOUNT_PREVIOUSLY_DECLINED') {
        showError('Account declined', err.message, 'warning');
      } else {
        showError('Quote Failed', err?.message || 'Could not confirm the bank payment fee. Please try again.', 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // One-time payment flow (Pay Now card) — prepare/validate, then open the
  // universal proceed modal. The actual API call lives in handlePayNow below
  // and is invoked when the user clicks "Yes, proceed".
  const preparePayNow = async () => {
    // Validate required fields including MSISDN
    if (!newBeneficiary.name || !newBeneficiary.msisdn || !paymentAmount) {
      showError('Validation Error', 'Please fill in all required fields: Name, Mobile Number (MSISDN), and Amount', 'warning');
      return;
    }

    const isBankPayment = selectedAccountType === 'eft' || selectedAccountType === 'payshap';

    // For bank payments, also require bank name and account number
    if (isBankPayment && (!newBeneficiary.bankName || !newBeneficiary.identifier)) {
      showError('Validation Error', 'For bank payments, please also select a bank and enter the account number', 'warning');
      return;
    }

    // Validate mobile number format using the same validation as registration/login
    if (!validateMobileNumber(newBeneficiary.msisdn)) {
      showError('Validation Error', 'Invalid South African mobile number format. Please enter a valid mobile number (e.g., 078 123 4567)', 'warning');
      return;
    }

    if (!isBankPayment && isOwnWalletPay) {
      showError('You cannot pay your own wallet', OWN_WALLET_PAY_MESSAGE, 'error');
      return;
    }

    if (isBankPayment && isDuplicateNewBankAccount) {
      showError('Bank account already saved', DUPLICATE_BANK_ACCOUNT_MESSAGE, 'error');
      return;
    }

    if (!isBankPayment) {
      const destCopy = walletDestinationModalCopy(newBeneficiary.msisdn)
        || walletDestinationModalCopy(newBeneficiary.identifier);
      if (destCopy) {
        showError(destCopy.title, destCopy.message, 'error');
        return;
      }
    } else {
      const acctCheck = validateSaBankAccountNumber(newBeneficiary.identifier, { bankName: newBeneficiary.bankName });
      if (!acctCheck.valid) {
        const copy = bankFieldModalCopy(acctCheck)!;
        showError(copy.title, copy.message, 'error');
        return;
      }
    }

    const amount = parseFloat(paymentAmount);

    if (isBankPayment) {
      const rail = instantPaymentEnabled ? 'payshap' : 'eft';
      await prepareBankRailPayment({
        flow: 'payNow',
        amount,
        rail,
        bankAccountId: selectedBankAccountId,
        onProceed: () => handlePayNow(),
      });
      return;
    }

    const payload: PaymentProceedPayload = {
      amountCents: randsToCents(amount),
      recipientLabel: newBeneficiary.name,
      recipientSubLabel: newBeneficiary.identifier
        ? `Wallet ••••${newBeneficiary.identifier.slice(-4)}`
        : undefined,
      contextLabel: 'Wallet to wallet transfer',
    };

    openProceed(payload, () => handlePayNow());
  };

  // Execute the Pay Now payment. Called by the proceed modal's onProceed.
  // Assumes validation + (for PayShap) quote already happened in preparePayNow.
  const handlePayNow = async () => {
    const isBankPayment = selectedAccountType === 'eft' || selectedAccountType === 'payshap';

    if (isBankPayment) {
      // Wallet-to-bank: default H2H EFT, optional instant payment rail.
      setIsProcessing(true);
      let oneTimeAccountRef = pendingOneTimeBankAccount;
      // Capture recipient details before the form is reset (used for the save prompt).
      const recipientName = newBeneficiary.name;
      const recipientAccountNumber = newBeneficiary.identifier;
      const recipientBankName = newBeneficiary.bankName;
      const recipientMsisdn = newBeneficiary.msisdn;
      const recipientBranchCode = SA_BANKS.find(bank => bank.name === newBeneficiary.bankName)?.branchCode;
      // True when this Pay Now created a throwaway account (typed recipient, not a saved one).
      const isOneTimeRecipient = !!pendingOneTimeBankAccount || !payNowSourceBeneficiary;
      try {
        const amount = parseFloat(paymentAmount);
        const ref = (paymentReference || '').trim().slice(0, 20);
        const rail = instantPaymentEnabled ? 'payshap' : 'eft';
        let bankAccountId = selectedBankAccountId;

        if (!bankAccountId) {
          oneTimeAccountRef = await createPayNowBankAccount();
          bankAccountId = oneTimeAccountRef.accountId;
        }

        const result = await apiService.submitWalletBankPayment(
          bankAccountId,
          amount,
          rail,
          ref || undefined,
          quoteNeedsRecipientId && recipientIdCheck.isValid
            ? { recipientIdType: recipientIdCheck.ottIdType || undefined, recipientIdNumber: recipientIdDraft.replace(/\s/g, '') }
            : undefined,
        );

        await fetchTransactions();

        // For one-time recipients, offer to save (unless already saved). For payments to an
        // already-saved beneficiary, no prompt. The temp account stays until the user decides.
        let promptToSave = false;
        if (isOneTimeRecipient && oneTimeAccountRef) {
          const alreadySaved = findContactByBankAccount(beneficiaries, recipientAccountNumber);
          if (alreadySaved) {
            await removeOneTimeBankAccount(oneTimeAccountRef);
          } else {
            promptToSave = true;
            setContactToSave({
              name: recipientName,
              accountType: 'bank',
              msisdn: recipientMsisdn,
              bankName: recipientBankName,
              accountNumber: recipientAccountNumber,
              branchCode: recipientBranchCode,
              tempAccountRef: oneTimeAccountRef,
            });
          }
        }

        await loadBeneficiaries();
        setShowPayNow(false);
        closeProceedModal();
        setNewBeneficiary({ name: '', msisdn: '', identifier: '', bankName: '', accountNumber: '' });
        setPaymentAmount('');
        setPaymentDescription('');
        setPaymentReference('');
        setIsRecurring(false);
        setIsOneTimeMode(false);
        setSelectedBankAccountId(null);
        if (!promptToSave) {
          // Keep the pending ref while the save prompt is open; otherwise clear it.
          setPendingOneTimeBankAccount(null);
        }
        setInstantPaymentEnabled(false);

        if (result.paymentId) {
          void presentPaymentOutcomeModal(result.paymentId);
        }
      } catch (err: any) {
        // Payment failed — never persist a throwaway one-time account.
        if (isOneTimeRecipient) {
          await removeOneTimeBankAccount(oneTimeAccountRef);
          setPendingOneTimeBankAccount(null);
          setSelectedBankAccountId(null);
        }
        logError('SendMoneyPage', 'Wallet-bank payment failed', err as Error);
        showError('Payment Failed', getWalletBankPaymentErrorMessage(err), 'error');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    setIsProcessing(true);
    try {
      // Compose banking-style description with optional reference (max 20 chars)
      const ref = (paymentReference || '').trim().slice(0, 20);
      const desc = (paymentDescription || '').trim();
      const combinedDescription = [ref ? `Ref:${ref}` : '', desc].filter(Boolean).join(' - ') || undefined;

      // Real API call to debit/credit wallets using PostgreSQL
      const nameForDesc = newBeneficiary.name?.trim();
      const descForBackend = [
        ref ? `Ref:${ref}` : undefined,
        desc || undefined,
      ].filter(Boolean).join(' | ');

      const destMsisdn = (newBeneficiary.msisdn || newBeneficiary.identifier || '').trim();
      if (msisdnEquals(destMsisdn, user?.phoneNumber)) {
        showError('You cannot pay your own wallet', OWN_WALLET_PAY_MESSAGE, 'error');
        setIsProcessing(false);
        return;
      }
      const destCopy = walletDestinationModalCopy(destMsisdn);
      if (destCopy) {
        showError(destCopy.title, destCopy.message, 'error');
        setIsProcessing(false);
        return;
      }

      const data = await apiService.sendWalletToWallet(
        destMsisdn,
        parseFloat(paymentAmount),
        descForBackend,
      );

      // Insert a simplified transaction row locally for instant feedback
      const txn: Transaction = {
        id: (data && data.transactionId) ? String(data.transactionId) : Date.now().toString(),
        beneficiaryName: newBeneficiary.name,
        amount: -parseFloat(paymentAmount), // Negative for sent money (debit)
        date: new Date(),
        status: 'completed',
        reference: data?.transactionId,
        accountType: 'mymoolah',
        description: `${newBeneficiary.name} | Ref:${paymentReference || 'Payment'}`, // Show recipient name and user reference
        kind: 'send',
        type: 'send', // Add type field
        metadata: {} // Add metadata field
      };
      // Refresh transactions from API
      await fetchTransactions();

      setShowPayNow(false);
      closeProceedModal();
      
      // Show "Save Beneficiary?" only for genuinely NEW MyMoolah recipients.
      // Uses normalized matching (e.g. +2782… vs 2782… vs 082…) so an already-saved
      // recipient is never re-prompted.
      const existingContact = findContactByMsisdn(beneficiaries, destMsisdn);

      if (!existingContact) {
        setContactToSave({
          name: newBeneficiary.name,
          accountType: 'mymoolah',
          msisdn: destMsisdn,
        });
      } else {
        // Already saved - update local stats and confirm success.
        setBeneficiaries(prev => prev.map(b =>
          contactMatchesMsisdn(b, destMsisdn)
            ? {
                ...b,
                lastPaid: new Date(),
                totalPaid: ((b as any).totalPaid || 0) + parseFloat(paymentAmount),
                paymentCount: ((b as any).paymentCount || 0) + 1
              }
            : b
        ));
        showError('Success', `Payment of ${formatCurrency(parseFloat(paymentAmount))} sent successfully to ${newBeneficiary.name}!`, 'info');
      }

      // reset one-time form
      setNewBeneficiary({ name: '', msisdn: '', identifier: '', bankName: '', accountNumber: '' });
      setPaymentAmount('');
      setPaymentDescription('');
      setPaymentReference('');
      setIsRecurring(false);
      setIsOneTimeMode(false);
    } catch (err: any) {
      logError('SendMoneyPage', 'Pay Now payment failed', err as Error);
      showError('Error', err?.message || 'Payment failed. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Persist the contact behind the shared "Save Beneficiary?" prompt to the backend
  // (unified beneficiaries). For one-time bank recipients the account already exists,
  // so saving just keeps it; declining removes it.
  const persistContactToSave = async () => {
    if (!contactToSave) return;

    if (contactToSave.tempAccountRef) {
      // One-time bank account already created to make the payment — keep it.
    } else if (contactToSave.accountType === 'mymoolah') {
      await beneficiaryService.createPaymentBeneficiary({
        name: contactToSave.name,
        msisdn: contactToSave.msisdn,
        accountType: 'mymoolah',
        role: 'beneficiary',
      });
    } else {
      await beneficiaryService.createPaymentBeneficiary({
        name: contactToSave.name,
        accountType: 'bank',
        bankName: contactToSave.bankName,
        accountNumber: contactToSave.accountNumber,
        branchCode: contactToSave.branchCode,
        mobile: contactToSave.msisdn,
        role: 'beneficiary',
      });
    }

    await loadBeneficiaries();
  };

  const declineContactToSave = () => {
    if (contactToSave?.tempAccountRef) {
      void removeOneTimeBankAccount(contactToSave.tempAccountRef);
      setPendingOneTimeBankAccount(null);
      setSelectedBankAccountId(null);
    }
  };

  // Handle Payment — prepare/validate, then open the universal proceed modal.
  // The actual API call lives in handlePayment below, invoked on "Yes, proceed".
  const preparePayment = async () => {
    if (!selectedBeneficiary || !paymentAmount) {
      showError('Validation Error', 'Please select a beneficiary and enter an amount', 'warning');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      showError('Validation Error', 'Please enter a valid amount', 'warning');
      return;
    }

    if (selectedBeneficiary.accountType === 'bank') {
      const selectedAccount = getSelectedAccount(selectedBeneficiary);
      const bankAccountId = selectedAccount?.type === 'bank'
        ? selectedAccount.id
        : selectedBeneficiary.accounts?.find(a => a.type === 'bank')?.id;
      if (!bankAccountId) {
        showError('Validation Error', 'Please select a bank account for this beneficiary', 'warning');
        return;
      }
      const rail = instantPaymentEnabled ? 'payshap' : 'eft';
      await prepareBankRailPayment({
        flow: 'savedBeneficiary',
        amount,
        rail,
        bankAccountId,
        onProceed: () => handlePayment(),
      });
      return;
    }

    const destMsisdn = resolveContactMobile(selectedBeneficiary) || selectedBeneficiary.msisdn || '';
    if (msisdnEquals(destMsisdn, user?.phoneNumber)) {
      showError('You cannot pay your own wallet', OWN_WALLET_PAY_MESSAGE, 'error');
      return;
    }
    const destCopy = walletDestinationModalCopy(destMsisdn)
      || walletDestinationModalCopy(selectedBeneficiary.identifier);
    if (destCopy) {
      showError(destCopy.title, destCopy.message, 'error');
      return;
    }

    const last4 = destMsisdn.slice(-4);

    const payload: PaymentProceedPayload = {
      amountCents: randsToCents(amount),
      recipientLabel: selectedBeneficiary.name,
      recipientSubLabel: last4 ? `Wallet ••••${last4}` : undefined,
      contextLabel: 'Wallet to wallet transfer',
    };

    openProceed(payload, () => handlePayment());
  };

  // Execute the saved-beneficiary payment. Called by the proceed modal's onProceed.
  // Assumes validation + (for PayShap) quote already happened in preparePayment.
  const handlePayment = async () => {
    if (!selectedBeneficiary || !paymentAmount) return;

    setIsProcessing(true);

    try {
      const ref = (paymentReference || '').trim().slice(0, MAX_REFERENCE_LENGTH);
      const desc = (paymentDescription || '').trim();
      const descForBackend = [
        ref ? `Ref:${ref}` : undefined,
        desc || undefined,
      ].filter(Boolean).join(' | ');

      // Route bank beneficiaries through wallet-to-bank EFT by default.
      if (selectedBeneficiary.accountType === 'bank') {
        const amount = parseFloat(paymentAmount);
        const selectedAccount = getSelectedAccount(selectedBeneficiary);
        const bankAccountId = selectedAccount?.type === 'bank' ? selectedAccount.id : selectedBeneficiary.accounts?.find(a => a.type === 'bank')?.id;
        if (!bankAccountId) {
          throw new Error('Please select a bank account for this beneficiary');
        }
        const result = await apiService.submitWalletBankPayment(
          bankAccountId,
          amount,
          instantPaymentEnabled ? 'payshap' : 'eft',
          ref || undefined,
          quoteNeedsRecipientId && recipientIdCheck.isValid
            ? { recipientIdType: recipientIdCheck.ottIdType || undefined, recipientIdNumber: recipientIdDraft.replace(/\s/g, '') }
            : undefined,
        );
        await fetchTransactions();
        setBeneficiaries(prev => prev.map(b =>
          b.id === selectedBeneficiary.id
            ? { ...b, lastPaid: new Date(), totalPaid: ((b as any).totalPaid || 0) + amount, paymentCount: ((b as any).paymentCount || 0) + 1 }
            : b
        ));
        setPaymentAmount('');
        setPaymentDescription('');
        setPaymentReference('');
        setIsRecurring(false);
        setShowPaymentModal(false);
        setSelectedBeneficiary(null);
        setInstantPaymentEnabled(false);
        closeProceedModal();
        if (result.paymentId) {
          void presentPaymentOutcomeModal(result.paymentId);
        }
        return;
      }

      // MyMoolah wallet-to-wallet
      const destMsisdn = resolveContactMobile(selectedBeneficiary) || selectedBeneficiary.msisdn || '';
      if (msisdnEquals(destMsisdn, user?.phoneNumber)) {
        showError('You cannot pay your own wallet', OWN_WALLET_PAY_MESSAGE, 'error');
        return;
      }
      const destCopy = walletDestinationModalCopy(destMsisdn)
        || walletDestinationModalCopy(selectedBeneficiary.identifier);
      if (destCopy) {
        showError(destCopy.title, destCopy.message, 'error');
        return;
      }
      const data = await apiService.sendWalletToWallet(
        destMsisdn,
        parseFloat(paymentAmount),
        descForBackend,
      );

      // Create transaction record for UI
      const transaction: Transaction = {
        id: (data && data.transactionId) ? String(data.transactionId) : Date.now().toString(),
        beneficiaryId: selectedBeneficiary.id,
        beneficiaryName: selectedBeneficiary.name,
        amount: -parseFloat(paymentAmount), // Negative for sent money (debit)
        date: new Date(),
        status: 'completed',
        reference: data?.transactionId || `${TRANSACTION_PREFIX}${Date.now().toString().slice(-TRANSACTION_ID_LENGTH)}`,
        accountType: selectedBeneficiary.accountType,
        description: `Sent to ${selectedBeneficiary.name}`, // Keep original for icon logic
        type: 'send' // Add type field
      };

      // Refresh transactions from API
      await fetchTransactions();

      // Update beneficiary stats
      setBeneficiaries(prev => prev.map(b =>
        b.id === selectedBeneficiary.id
          ? {
              ...b,
              lastPaid: new Date(),
              totalPaid: ((b as any).totalPaid || 0) + parseFloat(paymentAmount),
              paymentCount: ((b as any).paymentCount || 0) + 1
            }
          : b
      ));

      setPaymentAmount('');
      setPaymentDescription('');
      setPaymentReference('');
      setIsRecurring(false);
      setShowPaymentModal(false);
      setSelectedBeneficiary(null);
      closeProceedModal();

      showError('Success', `Payment of ${formatCurrency(parseFloat(paymentAmount))} sent successfully to ${selectedBeneficiary.name}!`, 'info');

    } catch (error: any) {
      logError('SendMoneyPage', 'Payment processing failed', error as Error);
      showError('Error', selectedBeneficiary?.accountType === 'bank' ? getWalletBankPaymentErrorMessage(error) : (error?.message || 'Payment failed. Please try again.'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format currency - delegates to the shared util so every wallet flow
  // formats ZAR identically (locale en-ZA, 2 decimals, "R " prefix).
  const formatCurrency = formatCurrencyShared;

  // Format voucher numbers - consistent with Dashboard and Transaction History
  const formatVoucherNumber = (description: string): string => {
    // Check if description contains a voucher number (12-16 digits)
    const voucherMatch = description.match(/(\d{12,16})/);
    if (voucherMatch) {
      const voucherNumber = voucherMatch[1];
      
      // Format based on length
      let formattedVoucher: string;
      if (voucherNumber.length === 14) {
        // EasyPay PIN (14 digits): 9 1234 0371 6648 2
        formattedVoucher = voucherNumber.slice(0, 1) + ' ' + 
                          voucherNumber.slice(1, 5) + ' ' + 
                          voucherNumber.slice(5, 9) + ' ' + 
                          voucherNumber.slice(9, 13) + ' ' + 
                          voucherNumber.slice(13);
      } else if (voucherNumber.length === 16) {
        // MMVoucher PIN (16 digits): 9562 4205 7827 9406
        formattedVoucher = voucherNumber.slice(0, 4) + ' ' + 
                          voucherNumber.slice(4, 8) + ' ' + 
                          voucherNumber.slice(8, 12) + ' ' + 
                          voucherNumber.slice(12);
      } else {
        // Fallback for other lengths: groups of 4
        formattedVoucher = voucherNumber.slice(0, 4) + ' ' + 
                          voucherNumber.slice(4, 8) + ' ' + 
                          voucherNumber.slice(8);
      }
      
      return description.replace(voucherNumber, formattedVoucher);
    }
    return description;
  };

  // Format date - consistent with Dashboard
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  // Get account type badge
  const getAccountTypeBadge = (accountType: 'mymoolah' | 'bank') => {
    return accountType === 'mymoolah' ? (
      <Badge className="bg-[#86BE41]/10 text-[#86BE41] border-[#86BE41]/20" style={{ fontSize: '10px' }}>
        <Wallet className="w-3 h-3 mr-1" />
        MyMoolah
      </Badge>
    ) : (
      <Badge className="bg-[#2D8CCA]/10 text-[#2D8CCA] border-[#2D8CCA]/20" style={{ fontSize: '10px' }}>
        <Building2 className="w-3 h-3 mr-1" />
        Bank
      </Badge>
    );
  };

  return (
    <div style={{ 
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      {/* Page Title Section */}
      <div style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative'
      }}>
        <button 
          onClick={() => navigate('/transact')}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            padding: '0',
            minHeight: '44px',
            minWidth: '44px',
            position: 'absolute',
            left: '16px',
            zIndex: 1
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <ArrowLeft style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </button>
        <h1 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '18px',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0',
          textAlign: 'center',
          width: '100%'
        }}>
          Send Money
        </h1>
        {/* Wallet Balance Badge */}
        <div 
          style={{
            position: 'absolute',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '44px'
          }}
        >
          <Badge 
            style={{
              backgroundColor: '#86BE41',
              color: '#ffffff',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '9.18px',
              fontWeight: '600',
              padding: '3.06px 6.12px',
              borderRadius: '9.18px',
              border: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            {walletBalance}
          </Badge>
        </div>
      </div>

      {/* Search and Add Section */}
      <div style={{
        padding: '16px',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              color: '#9ca3af'
            }} />
            <Input
              placeholder="Search beneficiaries"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-base)',
                height: '44px',
                paddingLeft: '40px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#ffffff'
              }}
            />
          </div>
          <Dialog open={showAddBeneficiary} onOpenChange={setShowAddBeneficiary}>
            <DialogTrigger asChild>
              <Button style={{
                backgroundColor: '#86BE41',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '0 16px',
                height: '44px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: 'var(--mobile-font-base)',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7AB139'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#86BE41'}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm mx-auto" aria-describedby="add-beneficiary-desc">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Add New Beneficiary
                </DialogTitle>
                <DialogDescription id="add-beneficiary-desc">
                  Add a new beneficiary for future payments
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Payment Method Selection */}
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Method</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {/* 1. MyMoolah — wallet-to-wallet */}
                    <Button
                      variant={selectedAccountType === 'mymoolah' ? 'default' : 'outline'}
                      onClick={() => {
                        setSelectedAccountType('mymoolah');
                        setInstantPaymentEnabled(false);
                      }}
                      className={`h-16 flex-col gap-0.5 ${selectedAccountType === 'mymoolah' ? 'bg-[#86BE41] text-white border-[#86BE41]' : 'border-gray-200 text-gray-700'}`}
                    >
                      <Wallet className="w-5 h-5" />
                      <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>MyMoolah</span>
                    </Button>
                    {/* 2. Bank Transfer */}
                    <Button
                      variant={selectedAccountType === 'payshap' ? 'default' : 'outline'}
                      onClick={() => {
                        setSelectedAccountType('payshap');
                        setInstantPaymentEnabled(false);
                      }}
                      className={`h-16 flex-col gap-0.5 ${selectedAccountType === 'payshap' ? 'bg-[#2D8CCA] text-white border-[#2D8CCA]' : 'border-gray-200 text-gray-700'}`}
                    >
                      <Building2 className="w-5 h-5" />
                      <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Bank Transfer</span>
                    </Button>
                    {/* 3. MoolahMove */}
                    <Button
                      variant={selectedAccountType === 'moolahmove' ? 'default' : 'outline'}
                      disabled={!MOOLAHMOVE_ENABLED}
                      onClick={() => {
                        if (!MOOLAHMOVE_ENABLED) return;
                        setSelectedAccountType('moolahmove');
                        setInstantPaymentEnabled(false);
                      }}
                      className={`h-16 flex-col gap-0.5 col-span-2 ${
                        !MOOLAHMOVE_ENABLED
                          ? 'border-gray-100 bg-gray-50 text-gray-400 opacity-70 cursor-not-allowed'
                          : selectedAccountType === 'moolahmove'
                          ? 'bg-[#65AEDD] text-white border-[#65AEDD]'
                          : 'border-[#65AEDD] text-[#2D8CCA] bg-white hover:bg-[#65AEDD]/10'
                      }`}
                    >
                      <Globe className="w-5 h-5" />
                      <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>MoolahMove</span>
                      {!MOOLAHMOVE_ENABLED && (
                        <span style={{ fontSize: '8px', fontFamily: 'Montserrat, sans-serif', background: '#65AEDD', color: '#ffffff', borderRadius: '999px', padding: '1px 6px', lineHeight: '1.4', fontWeight: 600 }}>Coming Soon</span>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-3">
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Beneficiary Name
                    </Label>
                    <Input
                      placeholder="Enter full name"
                      value={newBeneficiary.name}
                      onChange={(e) => setNewBeneficiary(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-base)',
                        height: 'var(--mobile-touch-target)'
                      }}
                    />
                  </div>

                  {selectedAccountType === 'moolahmove' && (
                    <>
                      <div style={{
                        background: 'linear-gradient(135deg, #F59E0B15, #F59E0B08)',
                        border: '1px solid #F59E0B40',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Globe style={{ width: '14px', height: '14px', color: '#F59E0B', flexShrink: 0 }} />
                        <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', color: '#92400E', margin: 0 }}>
                          Send money internationally via <strong>MoolahMove</strong>. Recipient receives local currency.
                        </p>
                      </div>

                      <div>
                        <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          Country <span className="text-red-500">*</span>
                        </Label>
                        <Select value={newMoolahMoveCountry} onValueChange={setNewMoolahMoveCountry}>
                          <SelectTrigger style={{ height: 'var(--mobile-touch-target)', marginTop: '6px' }}>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {MOOLAHMOVE_COUNTRIES.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.flag} {country.name} ({country.currency})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {newMoolahMoveCountry && (
                        <div>
                          <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Payment Method <span className="text-red-500">*</span>
                          </Label>
                          <Select value={newMoolahMoveChannelId} onValueChange={setNewMoolahMoveChannelId}>
                            <SelectTrigger style={{ height: 'var(--mobile-touch-target)', marginTop: '6px' }}>
                              <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                            <SelectContent>
                              {newMoolahMoveChannels.map((channel) => (
                                <SelectItem key={channel.id} value={channel.id}>
                                  {channel.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {newMoolahMoveChannelId && (
                        <div>
                          <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {newMoolahMoveChannel?.type === 'mobile_money' ? 'Mobile Money Number' : 'Account Number'} <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder={newMoolahMoveChannel?.type === 'mobile_money' ? 'e.g. +265 99 123 4567' : 'Bank account number'}
                            value={newMoolahMoveAccountNumber}
                            onChange={(e) => setNewMoolahMoveAccountNumber(e.target.value)}
                            style={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: 'var(--mobile-font-base)',
                              height: 'var(--mobile-touch-target)',
                              marginTop: '6px'
                            }}
                            className="font-mono"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Mobile Number Field - For MyMoolah: this IS their account number. For Bank: this is MSISDN for verification */}
                  {selectedAccountType !== 'moolahmove' && (
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {selectedAccountType === 'mymoolah' 
                        ? 'Mobile Number (Account Number)' 
                        : 'Mobile Number (MSISDN)'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="e.g., 078 123 4567"
                      value={selectedAccountType === 'mymoolah' ? (newBeneficiary.identifier || newBeneficiary.msisdn) : newBeneficiary.msisdn}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Only allow digits, spaces, and hyphens for mobile numbers
                        const cleanValue = value.replace(/[^\d\s-]/g, '');
                        
                        if (selectedAccountType === 'mymoolah') {
                          // For MyMoolah: update both identifier (account number) and msisdn
                          setNewBeneficiary(prev => ({ 
                            ...prev, 
                            identifier: cleanValue,
                            msisdn: cleanValue 
                          }));
                        } else {
                          // For Bank: only update msisdn
                          setNewBeneficiary(prev => ({ ...prev, msisdn: cleanValue }));
                        }
                      }}

                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-base)',
                        height: 'var(--mobile-touch-target)'
                      }}
                      className="font-mono"
                    />
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-small)',
                      color: '#6b7280',
                      marginTop: '0.5rem'
                    }}>
                      {selectedAccountType === 'mymoolah' 
                        ? 'This mobile number is their MyMoolah account number' 
                        : 'Required for FICA compliance and verification'}
                    </p>
                    {/* Real-time validation feedback */}
                    {selectedAccountType === 'mymoolah' && (newBeneficiary.identifier || newBeneficiary.msisdn) && (
                      (() => {
                        const value = newBeneficiary.identifier || newBeneficiary.msisdn;
                        const isValid = validateMobileNumber(value);
                        if (!isValid) {
                          return (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                              <p className="text-sm text-red-600 font-medium">Invalid mobile number format</p>
                              <p className="text-xs text-red-500 mt-1">Please enter a valid South African mobile number (e.g., 078 123 4567)</p>
                            </div>
                          );
                        }
                        if (isOwnWalletPay) {
                          return (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                              <p className="text-sm text-red-600 font-medium">{OWN_WALLET_PAY_MESSAGE}</p>
                            </div>
                          );
                        }
                        return (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                            <p className="text-sm text-green-600 font-medium">✓ Valid mobile number</p>
                          </div>
                        );
                      })()
                    )}
                  </div>
                  )}

                  {selectedAccountType === 'payshap' && (
                    <>
                      <div>
                        <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          Bank
                        </Label>
                        <Select value={newBeneficiary.bankName} onValueChange={(value) => 
                          setNewBeneficiary(prev => ({ ...prev, bankName: value }))
                        }>
                          <SelectTrigger style={{ height: 'var(--mobile-touch-target)' }}>
                            <SelectValue placeholder="Select bank" />
                          </SelectTrigger>
                          <SelectContent>
                            {SA_BANKS.map((bank) => (
                              <SelectItem key={bank.code} value={bank.name}>
                                {bank.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          Account Number
                        </Label>
                        <Input
                          placeholder="Enter account number"
                          value={newBeneficiary.identifier}
                          onChange={(e) => setNewBeneficiary(prev => ({ ...prev, identifier: e.target.value }))}
                          style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: 'var(--mobile-font-base)',
                            height: 'var(--mobile-touch-target)'
                          }}
                        />
                        {isDuplicateNewBankAccount && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600 font-medium">{DUPLICATE_BANK_ACCOUNT_MESSAGE}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddBeneficiary(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddBeneficiary}
                    disabled={
                      selectedAccountType === 'moolahmove'
                        ? !newBeneficiary.name || !newMoolahMoveCountry || !newMoolahMoveChannelId || !newMoolahMoveAccountNumber
                        : !newBeneficiary.name || !newBeneficiary.msisdn || isOwnWalletPay || isDuplicateNewBankAccount
                    }
                    className="flex-1 bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white"
                  >
                    Add Beneficiary
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
            <SelectTrigger style={{
              flex: 1,
              height: '44px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-base)'
            }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
                              <SelectItem value="lastPaid">Most Recent Activity</SelectItem>
              <SelectItem value="alphabetical">Alphabetical</SelectItem>
              <SelectItem value="favorite">Favorites First</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
            <SelectTrigger style={{
              flex: 1,
              height: '44px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--mobile-font-base)'
            }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show All</SelectItem>
              <SelectItem value="mymoolah">MyMoolah Only</SelectItem>
              <SelectItem value="bank">Bank Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {/* Quick Filter Tabs */}
        <div style={{
          display: 'flex',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          padding: '4px',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => { setFilterType('all'); setIsOneTimeMode(false); }}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: 'var(--mobile-font-small)',
              fontWeight: '500',
              fontFamily: 'Montserrat, sans-serif',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: filterType === 'all' ? '#2D8CCA' : 'transparent',
              color: filterType === 'all' ? '#ffffff' : '#6b7280'
            }}
            onMouseOver={(e) => {
              if (filterType !== 'all') {
                e.currentTarget.style.color = '#1f2937';
              }
            }}
            onMouseOut={(e) => {
              if (filterType !== 'all') {
                e.currentTarget.style.color = '#6b7280';
              }
            }}
          >
            Frequent
          </button>
          <button
            onClick={() => {
              setFilterType('mymoolah');
              setPayNowSourceBeneficiary(null);
              setSelectedBankAccountId(null);
              setSelectedAccountType('mymoolah');
              setInstantPaymentEnabled(false);
              setNewBeneficiary({ name: '', msisdn: '', identifier: '', bankName: '', accountNumber: '' });
              setIsOneTimeMode(true);
              setShowPayNow(true);
            }}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: 'var(--mobile-font-small)',
              fontWeight: '500',
              fontFamily: 'Montserrat, sans-serif',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: isOneTimeMode ? '#2D8CCA' : 'transparent',
              color: isOneTimeMode ? '#ffffff' : '#6b7280'
            }}
            onMouseOver={(e) => {
              if (!isOneTimeMode) {
                e.currentTarget.style.color = '#1f2937';
              }
            }}
            onMouseOut={(e) => {
              if (!isOneTimeMode) {
                e.currentTarget.style.color = '#6b7280';
              }
            }}
          >
            Pay Now
          </button>
        </div>

        {/* Beneficiaries by Recent Activity */}
        <div className="mb-4">
          <h3 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
            fontWeight: 'var(--font-weight-bold)',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            Your Beneficiaries
          </h3>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'var(--mobile-font-small)',
            color: '#6b7280',
            margin: '0 0 16px 0'
          }}>
            Organized by most recent activity • {filteredBeneficiaries.length} total
          </p>
        </div>
        
        <div className="space-y-3 mb-8">
          {filteredBeneficiaries.map((beneficiary) => {
            const hasMultipleAccounts = beneficiary.accounts && beneficiary.accounts.length > 1;
            const selectedAccount = getSelectedAccount(beneficiary);
            const isExpanded = expandedAccountSelectors[beneficiary.id] || false;
            const displayAccount = selectedAccount || null;
            const accountType = displayAccount 
              ? (displayAccount.type === 'bank' ? 'bank' : 'mymoolah')
              : beneficiary.accountType;
            const accountIdentifier = displayAccount 
              ? displayAccount.identifier 
              : beneficiary.identifier;
            const accountBankName = displayAccount?.metadata?.bankName || beneficiary.bankName;
            
            return (
              <div key={beneficiary.id} className="space-y-2">
            <Card 
              className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    if (hasMultipleAccounts && !isExpanded) {
                      toggleAccountSelector(beneficiary.id);
                    } else {
                      prefillFromBeneficiary(beneficiary);
                    }
                  }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 bg-[#2D8CCA] rounded-full flex items-center justify-center text-white font-bold">
                    {beneficiary.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>

                  {/* Beneficiary Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-base)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: '#1f2937'
                      }}>
                        {beneficiary.name}
                      </h3>
                      {beneficiary.isFavorite && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}
                          {hasMultipleAccounts && (
                            <Badge 
                              variant="secondary"
                              style={{
                                fontSize: '10px',
                                backgroundColor: '#e2e8f0',
                                color: '#6b7280',
                                padding: '2px 6px'
                              }}
                            >
                              {(beneficiary as any).accounts?.length || 0} accounts
                            </Badge>
                          )}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-1">
                          {getAccountTypeBadge(accountType)}
                    </div>

                        {/* Account Identifier */}
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-small)',
                      color: '#6b7280'
                    }}>
                          {accountType === 'mymoolah' ? (
                            <>📱 {accountIdentifier}</>
                          ) : (
                            <>
                              🏦 {accountBankName || 'Bank'} • {accountIdentifier}
                            </>
                          )}
                    </p>

                    {beneficiary.lastPaid ? (
                      <p style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-small)',
                        color: '#6b7280'
                      }}>
                        Last paid: {formatDate(beneficiary.lastPaid)}
                      </p>
                    ) : (
                      <p style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-small)',
                        color: '#6b7280'
                      }}>
                        Never paid
                      </p>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-col items-end gap-2">
                        {/* Account Selector Toggle (if multiple accounts) */}
                        {hasMultipleAccounts && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="w-8 h-8 p-0 hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAccountSelector(beneficiary.id);
                            }}
                            style={{
                              minWidth: '32px',
                              minHeight: '32px'
                            }}
                          >
                            <ChevronDown 
                              className="w-4 h-4 text-gray-500"
                              style={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                              }}
                            />
                          </Button>
                        )}
                        
                    {/* Pay Button */}
                    <Button
                      size="sm"
                      className="bg-[#86BE41] hover:bg-[#7AB139] text-white px-3 py-1"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            prefillFromBeneficiary(beneficiary); 
                          }}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Pay
                    </Button>
                    
                    {/* Edit, Add Account & Remove Buttons */}
                    <div className="flex items-center gap-1">
                      {/* Add Account button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-8 h-8 p-0 hover:bg-green-50"
                        title="Add account"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddAccountTarget(beneficiary);
                          setShowAddAccountModal(true);
                        }}
                        style={{ minWidth: '32px', minHeight: '32px' }}
                      >
                        <PlusCircle className="w-3 h-3 text-gray-500 hover:text-[#86BE41]" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-8 h-8 p-0 hover:bg-gray-100"
                        onClick={(e) => { e.stopPropagation(); handleEditBeneficiary(beneficiary); }}
                        style={{
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        <Edit2 className="w-3 h-3 text-gray-500 hover:text-[#2D8CCA]" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-8 h-8 p-0 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleRemoveBeneficiary(beneficiary); }}
                        style={{
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        <X className="w-3 h-3 text-gray-500 hover:text-red-500" />
                      </Button>
                    </div>
                    
                    {/* Payment Count */}
                    {((beneficiary as any).paymentCount || 0) > 0 && (
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '10px',
                        color: '#6b7280'
                      }}>
                        {beneficiary.paymentCount} payments
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
                
                {/* Account Selector Dropdown (when expanded) */}
                {hasMultipleAccounts && isExpanded && beneficiary.accounts && (
                  <div
                    className="account-selector-dropdown"
                    style={{
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      marginTop: '-8px',
                      marginLeft: '16px',
                      marginRight: '16px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.2s ease-out',
                      opacity: 1,
                      transform: 'translateY(0)'
                    }}
                  >
                    {beneficiary.accounts.map((account) => {
                      const isSelected = selectedAccountIds[beneficiary.id] === account.id || 
                        (!selectedAccountIds[beneficiary.id] && account.isDefault);
                      return (
                        <div
                          key={account.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectAccount(beneficiary.id, account.id);
                            prefillFromBeneficiary(beneficiary, account.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              selectAccount(beneficiary.id, account.id);
                              prefillFromBeneficiary(beneficiary, account.id);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            backgroundColor: isSelected ? 'rgba(134, 190, 65, 0.1)' : 'transparent',
                            border: isSelected ? '1px solid #86BE41' : '1px solid transparent',
                            cursor: 'pointer',
                            marginBottom: '4px',
                            textAlign: 'left',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {account.type === 'mymoolah' ? (
                              <Wallet className="w-4 h-4 text-[#86BE41]" />
                            ) : (
                              <Building2 className="w-4 h-4 text-[#2D8CCA]" />
                            )}
                            <div>
                              <p style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#1f2937',
                                margin: 0
                              }}>
                                {account.type === 'mymoolah' 
                                  ? 'MyMoolah Wallet'
                                  : `${account.metadata?.bankName || 'Bank'} Account`}
                              </p>
                              <p style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '12px',
                                color: '#6b7280',
                                margin: 0
                              }}>
                                {account.identifier}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {isSelected && (
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#86BE41',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#ffffff'
                                }} />
                              </div>
                            )}
                            {/* Remove this account (only if beneficiary has >1 account) */}
                            {(beneficiary.accounts?.length ?? 0) > 1 && (
                              <button
                                type="button"
                                title="Remove this account"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const label = account.type === 'mymoolah'
                                    ? `MyMoolah Wallet (${account.identifier})`
                                    : `${account.metadata?.bankName || 'Bank'} ${account.identifier}`;
                                  setRemoveAccountTarget({
                                    beneficiary,
                                    accountId: account.id,
                                    accountType: account.type as 'mymoolah' | 'bank',
                                    label,
                                  });
                                  setShowRemoveAccountModal(true);
                                }}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Add another account shortcut */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddAccountTarget(beneficiary);
                        setShowAddAccountModal(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        border: '1px dashed #d1d5db',
                        cursor: 'pointer',
                        marginTop: '4px',
                        color: '#6b7280',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '12px',
                      }}
                    >
                      <PlusCircle className="w-3 h-3" />
                      Add another account
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>


      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-sm mx-auto" aria-describedby="pay-beneficiary-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Pay {selectedBeneficiary?.name}
            </DialogTitle>
            <DialogDescription id="pay-beneficiary-desc">
              Make a payment to {selectedBeneficiary?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBeneficiary && (
            <div className="space-y-4">
              {/* Beneficiary Summary */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2D8CCA] rounded-full flex items-center justify-center text-white font-bold">
                    {selectedBeneficiary.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-base)',
                      fontWeight: 'var(--font-weight-medium)'
                    }}>
                      {selectedBeneficiary.name}
                    </p>
                    {getAccountTypeBadge(selectedBeneficiary.accountType)}
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Amount (ZAR)</Label>
                <Input
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(e) => {
                    // Banking-grade: Preserve exact user input - NO auto-formatting
                    let inputValue = e.target.value;
                    
                    // Allow empty string, numbers, and single decimal point only
                    // Remove any currency symbols or spaces that user might type
                    inputValue = inputValue.replace(/[^\d.]/g, '');
                    
                    // Ensure only one decimal point
                    const parts = inputValue.split('.');
                    if (parts.length > 2) {
                      inputValue = parts[0] + '.' + parts.slice(1).join('');
                    }
                    
                    // Limit to 2 decimal places (preserve user intent)
                    if (parts.length === 2 && parts[1].length > 2) {
                      inputValue = parts[0] + '.' + parts[1].substring(0, 2);
                    }
                    
                    // Set exact value - no automatic modification
                    setPaymentAmount(inputValue);
                  }}
                  type="text"
                  inputMode="decimal"
                  onKeyDown={(e) => {
                    // Prevent browser auto-formatting quirks
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => {
                    // Prevent scroll-to-change number input values
                    e.currentTarget.blur();
                  }}
                  onBlur={(e) => {
                    // Optional: Format on blur only (not during typing)
                    const value = e.target.value.trim();
                    if (value) {
                      const num = parseFloat(value);
                      if (!isNaN(num) && num > 0 && value.includes('.')) {
                        setPaymentAmount(num.toFixed(2));
                      }
                    }
                  }}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    height: 'var(--mobile-touch-target)'
                  }}
                />
                {selectedBeneficiary?.accountType === 'bank' && (
                  <BankPaymentAmountHints
                    rail={instantPaymentEnabled ? 'payshap' : 'eft'}
                    amount={parseFloat(paymentAmount) || 0}
                    availableBalance={walletBalanceNumeric}
                    feeAmount={walletBankQuote?.feeAmount}
                    totalDebit={walletBankQuote?.totalDebit}
                    quoteLoading={bankQuoteLoading}
                  />
                )}
              </div>

              {/* Quick Amounts */}
              <div className="grid grid-cols-4 gap-2">
                {[50, 100, 200, 500].map(amount => (
                  <Button
                    key={amount}
                    variant="outline"
                    size="sm"
                    onClick={() => setPaymentAmount(amount.toString())}
                    className="text-[#2D8CCA] border-[#2D8CCA] hover:bg-[#2D8CCA] hover:text-white"
                  >
                    {formatCurrency(amount)}
                  </Button>
                ))}
              </div>

              {/* Reference (banking standard: 20 characters) */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Reference (Optional, max 20 chars)
                </Label>
                <Input
                  placeholder="e.g. Rent Aug"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value.slice(0, 20))}
                  maxLength={20}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    height: 'var(--mobile-touch-target)'
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Description (Optional)
                </Label>
                <Textarea
                  placeholder="What's this payment for?"
                  value={paymentDescription}
                  onChange={(e) => setPaymentDescription(e.target.value)}
                  rows={3}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)'
                  }}
                />
              </div>

              {/* Recurring Payment */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    fontWeight: 'var(--font-weight-medium)'
                  }}>
                    Set up recurring payment
                  </p>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-small)',
                    color: '#6b7280'
                  }}>
                    Pay this amount monthly
                  </p>
                </div>
                <Switch
                  id="recurring-payment"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                />
              </div>

              {selectedBeneficiary?.accountType === 'bank' && (
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Transfer Speed</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      aria-pressed={!instantPaymentEnabled}
                      onClick={() => setInstantPaymentEnabled(false)}
                      className={`rounded-xl border p-3 text-left transition min-h-[92px] ${
                        !instantPaymentEnabled
                          ? 'border-[#2D8CCA] bg-blue-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-[#2D8CCA]/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Building2 className={`w-4 h-4 ${!instantPaymentEnabled ? 'text-[#2D8CCA]' : 'text-gray-500'}`} />
                        <span className={`text-[10px] rounded-full px-2 py-0.5 ${!instantPaymentEnabled ? 'bg-[#2D8CCA] text-white' : 'bg-gray-100 text-gray-500'}`}>
                          Default
                        </span>
                      </div>
                      <p className="font-semibold text-sm text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Standard EFT</p>
                      <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Normal bank transfer timing</p>
                      <p className="text-[10px] text-gray-400 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {!instantPaymentEnabled && walletBankQuote?.rail === 'eft' && walletBankQuote.feeAmount
                          ? `${formatCurrency(walletBankQuote.feeAmount)} fee`
                          : 'Includes transaction fee'}
                      </p>
                    </button>
                    <button
                      type="button"
                      aria-pressed={instantPaymentEnabled}
                      onClick={() => setInstantPaymentEnabled(true)}
                      className={`rounded-xl border p-3 text-left transition min-h-[92px] ${
                        instantPaymentEnabled
                          ? 'border-[#2D8CCA] bg-blue-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-[#2D8CCA]/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Send className={`w-4 h-4 ${instantPaymentEnabled ? 'text-[#2D8CCA]' : 'text-gray-500'}`} />
                        <span className={`text-[10px] rounded-full px-2 py-0.5 ${instantPaymentEnabled ? 'bg-[#2D8CCA] text-white' : 'bg-gray-100 text-gray-500'}`}>
                          Faster
                        </span>
                      </div>
                      <p className="font-semibold text-sm text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Instant Payment</p>
                      <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Faster bank transfer</p>
                      <p className="text-[10px] text-gray-400 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {instantPaymentEnabled && walletBankQuote?.rail === 'payshap' && walletBankQuote.feeAmount
                          ? `${formatCurrency(walletBankQuote.feeAmount)} fee`
                          : 'Includes transaction fee'}
                      </p>
                    </button>
                  </div>
                  {instantPaymentEnabled && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {INSTANT_PAYMENT_RAIL_HINT}
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => preparePayment()}
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || isProcessing}
                  className="flex-1 bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Pay {paymentAmount ? formatCurrency(parseFloat(paymentAmount)) : 'R 0.00'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* One-time Pay Now Modal (reuses add-beneficiary fields) */}
      <Dialog open={showPayNow} onOpenChange={(v) => {
        setShowPayNow(v);
        if (!v) {
          void removeOneTimeBankAccount(pendingOneTimeBankAccount);
          setIsOneTimeMode(false);
          setSelectedBankAccountId(null);
          setInstantPaymentEnabled(false);
          setWalletBankQuote(null);
          setPendingOneTimeBankAccount(null);
          setPayNowSourceBeneficiary(null);
        }
      }}>
        <DialogContent className="max-w-sm mx-auto" aria-describedby="pay-now-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Pay Now
            </DialogTitle>
            <DialogDescription id="pay-now-desc">
              Make a one-time payment to a new recipient
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Method Selection */}
            <div className="space-y-2">
              <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Method</Label>
              <div className="grid grid-cols-2 gap-2">
                {/* 1. MyMoolah — wallet-to-wallet */}
                <Button
                  variant={selectedAccountType === 'mymoolah' ? 'default' : 'outline'}
                  onClick={() => applyPayNowMethod('mymoolah')}
                  className={`h-16 flex-col gap-0.5 ${selectedAccountType === 'mymoolah' ? 'bg-[#86BE41] text-white border-[#86BE41]' : 'border-gray-200 text-gray-700'}`}
                >
                  <Wallet className="w-5 h-5" />
                  <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>MyMoolah</span>
                </Button>
                {/* 2. Bank Transfer */}
                <Button
                  variant={selectedAccountType === 'eft' || selectedAccountType === 'payshap' ? 'default' : 'outline'}
                  onClick={() => applyPayNowMethod('bank')}
                  className={`h-16 flex-col gap-0.5 ${selectedAccountType === 'eft' || selectedAccountType === 'payshap' ? 'bg-[#2D8CCA] text-white border-[#2D8CCA]' : 'border-gray-200 text-gray-700'}`}
                >
                  <Building2 className="w-5 h-5" />
                  <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Bank Transfer</span>
                </Button>
                {/* 3. MoolahMove */}
                <Button
                  variant="outline"
                  disabled={!MOOLAHMOVE_ENABLED}
                  onClick={() => {
                    if (!MOOLAHMOVE_ENABLED) return;
                    showError('MoolahMove', 'MoolahMove account setup is available for UAT testing from an existing beneficiary account.', 'info');
                  }}
                  className={`h-16 flex-col gap-0.5 col-span-2 ${
                    !MOOLAHMOVE_ENABLED
                      ? 'border-gray-100 bg-gray-50 text-gray-400 opacity-70 cursor-not-allowed'
                      : 'border-[#65AEDD] text-[#2D8CCA] bg-white hover:bg-[#65AEDD]/10'
                  }`}
                >
                  <Globe className="w-5 h-5" />
                  <span style={{ fontSize: '11px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>MoolahMove</span>
                  {!MOOLAHMOVE_ENABLED && (
                    <span style={{ fontSize: '8px', fontFamily: 'Montserrat, sans-serif', background: '#65AEDD', color: '#ffffff', borderRadius: '999px', padding: '1px 6px', lineHeight: '1.4', fontWeight: 600 }}>Coming Soon</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Recipient fields */}
            <div className="space-y-3">
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Beneficiary Name
                </Label>
                <Input
                  placeholder="Enter full name"
                  value={newBeneficiary.name}
                  onChange={(e) => setNewBeneficiary(prev => ({ ...prev, name: e.target.value }))}
                  style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', height: 'var(--mobile-touch-target)' }}
                />
              </div>
              
              {/* Mobile Number Field - For MyMoolah: this IS their account number. For bank transfers: MSISDN for FICA */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {selectedAccountType === 'mymoolah' 
                    ? 'Mobile Number (Account Number)' 
                    : 'Mobile Number (MSISDN)'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="e.g., 078 123 4567"
                  value={selectedAccountType === 'mymoolah' ? (newBeneficiary.identifier || newBeneficiary.msisdn) : newBeneficiary.msisdn}
                  onChange={(e) => {
                    const value = e.target.value;
                    const cleanValue = value.replace(/[^\d\s-]/g, '');
                    if (selectedAccountType === 'mymoolah') {
                      setNewBeneficiary(prev => ({ ...prev, identifier: cleanValue, msisdn: cleanValue }));
                    } else {
                      setNewBeneficiary(prev => ({ ...prev, msisdn: cleanValue }));
                    }
                  }}
                  style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', height: 'var(--mobile-touch-target)' }}
                  className="font-mono"
                />
                <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {selectedAccountType === 'mymoolah' 
                    ? 'This mobile number is their MyMoolah account number' 
                    : 'Required for FICA compliance and verification'}
                </p>
                {selectedAccountType === 'mymoolah' && (newBeneficiary.identifier || newBeneficiary.msisdn) && (
                  (() => {
                    const value = newBeneficiary.identifier || newBeneficiary.msisdn;
                    const isValid = validateMobileNumber(value);
                    if (!isValid) {
                      return (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-600 font-medium">Invalid mobile number format</p>
                          <p className="text-xs text-red-500 mt-1">Please enter a valid South African mobile number (e.g., 078 123 4567)</p>
                        </div>
                      );
                    }
                    if (isOwnWalletPay) {
                      return (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-600 font-medium">{OWN_WALLET_PAY_MESSAGE}</p>
                        </div>
                      );
                    }
                    return (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-600 font-medium">✓ Valid mobile number</p>
                      </div>
                    );
                  })()
                )}
              </div>

              {(selectedAccountType === 'eft' || selectedAccountType === 'payshap') && (
                <>
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Bank</Label>
                    <Select value={newBeneficiary.bankName} onValueChange={(value) => setNewBeneficiary(prev => ({ ...prev, bankName: value }))}>
                      <SelectTrigger style={{ height: 'var(--mobile-touch-target)' }}>
                        <SelectValue placeholder="Select bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {SA_BANKS.map((bank) => (
                          <SelectItem key={bank.code} value={bank.name}>
                            {bank.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Bank Account Number</Label>
                    <Input
                      placeholder="Enter account number"
                      value={newBeneficiary.identifier}
                      onChange={(e) => setNewBeneficiary(prev => ({ ...prev, identifier: e.target.value }))}
                      style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', height: 'var(--mobile-touch-target)' }}
                    />
                    {isDuplicateNewBankAccount && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600 font-medium">{DUPLICATE_BANK_ACCOUNT_MESSAGE}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Transfer Speed</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        aria-pressed={!instantPaymentEnabled}
                        onClick={() => {
                          setInstantPaymentEnabled(false);
                          setSelectedAccountType('eft');
                        }}
                        className={`rounded-xl border p-3 text-left transition min-h-[92px] ${
                          !instantPaymentEnabled
                            ? 'border-[#2D8CCA] bg-blue-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-[#2D8CCA]/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <Building2 className={`w-4 h-4 ${!instantPaymentEnabled ? 'text-[#2D8CCA]' : 'text-gray-500'}`} />
                          <span className={`text-[10px] rounded-full px-2 py-0.5 ${!instantPaymentEnabled ? 'bg-[#2D8CCA] text-white' : 'bg-gray-100 text-gray-500'}`}>
                            Default
                          </span>
                        </div>
                        <p className="font-semibold text-sm text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Standard EFT</p>
                        <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Normal bank transfer timing</p>
                        <p className="text-[10px] text-gray-400 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {!instantPaymentEnabled && walletBankQuote?.rail === 'eft' && walletBankQuote.feeAmount
                            ? `${formatCurrency(walletBankQuote.feeAmount)} fee`
                            : 'Includes transaction fee'}
                        </p>
                      </button>
                      <button
                        type="button"
                        aria-pressed={instantPaymentEnabled}
                        onClick={() => {
                          setInstantPaymentEnabled(true);
                          setSelectedAccountType('payshap');
                        }}
                        className={`rounded-xl border p-3 text-left transition min-h-[92px] ${
                          instantPaymentEnabled
                            ? 'border-[#2D8CCA] bg-blue-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-[#2D8CCA]/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <Send className={`w-4 h-4 ${instantPaymentEnabled ? 'text-[#2D8CCA]' : 'text-gray-500'}`} />
                          <span className={`text-[10px] rounded-full px-2 py-0.5 ${instantPaymentEnabled ? 'bg-[#2D8CCA] text-white' : 'bg-gray-100 text-gray-500'}`}>
                            Faster
                          </span>
                        </div>
                        <p className="font-semibold text-sm text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Instant Payment</p>
                        <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Faster bank transfer</p>
                        <p className="text-[10px] text-gray-400 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {instantPaymentEnabled && walletBankQuote?.rail === 'payshap' && walletBankQuote.feeAmount
                            ? `${formatCurrency(walletBankQuote.feeAmount)} fee`
                            : 'Includes transaction fee'}
                        </p>
                      </button>
                    </div>
                    {instantPaymentEnabled && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {INSTANT_PAYMENT_RAIL_HINT}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Amount */}
            <div>
              <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Amount (ZAR)</Label>
              <Input
                placeholder="0.00"
                value={paymentAmount}
                onChange={(e) => {
                  // Banking-grade: Preserve exact user input - NO auto-formatting
                  let inputValue = e.target.value;
                  
                  // Allow empty string, numbers, and single decimal point only
                  // Remove any currency symbols or spaces that user might type
                  inputValue = inputValue.replace(/[^\d.]/g, '');
                  
                  // Ensure only one decimal point
                  const parts = inputValue.split('.');
                  if (parts.length > 2) {
                    inputValue = parts[0] + '.' + parts.slice(1).join('');
                  }
                  
                  // Limit to 2 decimal places (preserve user intent)
                  if (parts.length === 2 && parts[1].length > 2) {
                    inputValue = parts[0] + '.' + parts[1].substring(0, 2);
                  }
                  
                  // Set exact value - no automatic modification
                  setPaymentAmount(inputValue);
                }}
                type="text"
                inputMode="decimal"
                onKeyDown={(e) => {
                  // Prevent browser auto-formatting quirks
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => {
                  // Prevent scroll-to-change number input values
                  e.currentTarget.blur();
                }}
                onBlur={(e) => {
                  // Optional: Format on blur only (not during typing)
                  const value = e.target.value.trim();
                  if (value) {
                    const num = parseFloat(value);
                    if (!isNaN(num) && num > 0 && value.includes('.')) {
                      setPaymentAmount(num.toFixed(2));
                    }
                  }
                }}
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', height: 'var(--mobile-touch-target)' }}
              />
              {(selectedAccountType === 'eft' || selectedAccountType === 'payshap') && (
                <BankPaymentAmountHints
                  rail={instantPaymentEnabled ? 'payshap' : 'eft'}
                  amount={parseFloat(paymentAmount) || 0}
                  availableBalance={walletBalanceNumeric}
                  feeAmount={walletBankQuote?.feeAmount}
                  totalDebit={walletBankQuote?.totalDebit}
                  quoteLoading={bankQuoteLoading}
                />
              )}
            </div>

            {/* Reference (banking standard: 20 characters) */}
            <div>
              <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Reference (Optional, max 20 chars)</Label>
              <Input
                placeholder="e.g. Rent Aug"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value.slice(0, 20))}
                maxLength={20}
                style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'var(--mobile-font-base)', height: 'var(--mobile-touch-target)' }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowPayNow(false)} className="flex-1" disabled={isProcessing}>Cancel</Button>
              <Button
                onClick={() => preparePayNow()}
                disabled={
                  !newBeneficiary.name ||
                  !(selectedAccountType === 'mymoolah' ? newBeneficiary.identifier : newBeneficiary.msisdn) ||
                  !paymentAmount ||
                  parseFloat(paymentAmount) <= 0 ||
                  isProcessing ||
                  isOwnWalletPay ||
                  isDuplicateNewBankAccount
                }
                className={`flex-1 text-white ${
                  selectedAccountType === 'mymoolah'
                    ? 'bg-[#86BE41] hover:bg-[#75aa35]'
                    : selectedAccountType === 'payshap'
                    ? 'bg-[#2D8CCA] hover:bg-[#2478b0]'
                    : 'bg-gradient-to-r from-[#86BE41] to-[#2D8CCA]'
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Pay Now</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-payment: shared Save Beneficiary prompt (persists to backend) */}
      <SaveContactPrompt
        isOpen={!!contactToSave}
        contactName={contactToSave?.name}
        roleLabel="beneficiary"
        outcomeText="Payment successful"
        onSave={persistContactToSave}
        onDecline={declineContactToSave}
        onClose={() => {
          setContactToSave(null);
          setPendingOneTimeBankAccount(null);
        }}
      />

      {/* Payment Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPaymentConfirmationModal}
        onClose={() => setShowPaymentConfirmationModal(false)}
        onConfirm={() => {
          if (beneficiaryForPayment) {
            setSelectedBeneficiary(beneficiaryForPayment);
            setShowPaymentModal(true);
          }
          setShowPaymentConfirmationModal(false);
        }}
        title="Recipient Added Successfully!"
        message="Would you like to make a payment now to"
        confirmText="Yes, make payment"
        cancelText="Not now"
        type="info"
        beneficiaryName={beneficiaryForPayment?.name}
      />

      {/* Universal pre-debit proceed confirmation modal.
          Replaces the legacy Instant Payment fee dialog and gates every
          outbound wallet debit (P2P, EFT, PayShap instant) behind a single
          "You are about to pay R{amount} to {recipient}. Proceed?" step.
          Rendered as a Radix Dialog so it nests correctly above the open
          Pay Now / Pay Beneficiary modals. */}
      <BankPaymentFeeAdjustModal
        open={showFeeAdjustModal}
        rail={feeAdjustContext?.rail ?? 'eft'}
        feeAmount={feeAdjustContext?.feeAmount ?? 0}
        availableBalance={feeAdjustContext?.availableBalance ?? 0}
        maxAffordableAmount={feeAdjustContext?.maxAffordableAmount ?? 0}
        onConfirm={handleFeeAdjustConfirm}
        onCancel={() => { void handleFeeAdjustCancel(); }}
      />

      <PaymentProceedModal
        open={showProceedModal}
        payload={proceedPayload}
        onProceed={handleProceed}
        onCancel={cancelProceed}
        isProcessing={isProcessing}
        recipientIdentityPrompt={quoteNeedsRecipientId}
        recipientIdValue={recipientIdDraft}
        onRecipientIdChange={setRecipientIdDraft}
        recipientIdValid={recipientIdCheck.isValid}
        recipientIdError={
          recipientIdDraft && !recipientIdCheck.isValid
            ? (recipientIdCheck.reason || 'Enter a valid SA ID or passport')
            : null
        }
      />

      {/* Edit Beneficiary Modal */}
      <Dialog open={showEditBeneficiaryModal} onOpenChange={(open) => {
        setShowEditBeneficiaryModal(open);
        if (!open) setEditingAccountOrigin(null);
      }}>
        <DialogContent className="max-w-sm mx-auto" aria-describedby="edit-beneficiary-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Edit Beneficiary
            </DialogTitle>
            <DialogDescription id="edit-beneficiary-desc">
              Edit beneficiary information
            </DialogDescription>
          </DialogHeader>
          
          {editingBeneficiary && (
            <div className="space-y-4">
              {/* Beneficiary Name */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Recipient Name</Label>
                <Input
                  placeholder="Enter recipient name"
                  value={editingBeneficiary.name}
                  onChange={(e) => setEditingBeneficiary(prev => prev ? { ...prev, name: e.target.value } : null)}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    height: 'var(--mobile-touch-target)'
                  }}
                />
              </div>

              {/* Mobile Number (MSISDN) - Required for MyMoolah, optional for Bank */}
              {editingBeneficiary.accountType !== 'bank' && (
              <div>
                  <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Mobile Number <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="e.g., 078 123 4567"
                  value={editingBeneficiary.msisdn || ''}
                  onChange={(e) => setEditingBeneficiary(prev => prev ? { ...prev, msisdn: e.target.value } : null)}
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    height: 'var(--mobile-touch-target)'
                  }}
                  className="font-mono"
                />
                <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  This number uniquely identifies the beneficiary
                </p>
              </div>
              )}

              {/* Account Type */}
              <div>
                <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Account Type</Label>
                <Select 
                  value={editingBeneficiary.accountType} 
                  onValueChange={(value: 'mymoolah' | 'bank') => 
                    setEditingBeneficiary(prev => prev ? { ...prev, accountType: value } : null)
                  }
                >
                  <SelectTrigger style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'var(--mobile-font-base)',
                    height: 'var(--mobile-touch-target)'
                  }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mymoolah">MyMoolah</SelectItem>
                    <SelectItem value="bank">Bank Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bank Name (only for bank accounts) */}
              {editingBeneficiary.accountType === 'bank' && (
                <>
                <div>
                  <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Bank Name</Label>
                  <Select 
                    value={editingBeneficiary.bankName || ''} 
                    onValueChange={(value) => 
                      setEditingBeneficiary(prev => prev ? { ...prev, bankName: value } : null)
                    }
                  >
                    <SelectTrigger style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-base)',
                      height: 'var(--mobile-touch-target)'
                    }}>
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {SA_BANKS.map(bank => (
                        <SelectItem key={bank.code} value={bank.name}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                  
                  {/* Account Number (only for bank accounts) */}
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif' }}>Account Number <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="e.g., 1234567890"
                      value={editingBeneficiary.identifier || ''}
                      onChange={(e) => {
                        // Only allow digits
                        const value = e.target.value.replace(/\D/g, '');
                        setEditingBeneficiary(prev => prev ? { ...prev, identifier: value } : null);
                      }}
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 'var(--mobile-font-base)',
                        height: 'var(--mobile-touch-target)'
                      }}
                      className="font-mono"
                    />
                    <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Enter the bank account number (8-12 digits)
                    </p>
                  </div>
                  
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowEditBeneficiaryModal(false);
                    setEditingAccountOrigin(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!editingBeneficiary) return;
                    
                    // Validate required fields
                    if (!editingBeneficiary.name?.trim()) {
                      showError('Validation Error', 'Recipient name is required', 'warning');
                      return;
                    }
                    
                    // MSISDN required for MyMoolah, optional for Bank
                    if (editingBeneficiary.accountType !== 'bank' && !editingBeneficiary.msisdn?.trim()) {
                      showError('Validation Error', 'Mobile number is required for MyMoolah accounts', 'warning');
                      return;
                    }
                    
                    if (editingBeneficiary.accountType === 'bank') {
                      if (!editingBeneficiary.bankName?.trim()) {
                        showError('Validation Error', 'Bank name is required for bank accounts', 'warning');
                        return;
                      }
                      if (!editingBeneficiary.identifier?.trim()) {
                        showError('Validation Error', 'Account number is required for bank accounts', 'warning');
                        return;
                      }
                      const acctCheck = validateSaBankAccountNumber(editingBeneficiary.identifier, {
                        bankName: editingBeneficiary.bankName,
                      });
                      if (!acctCheck.valid) {
                        const copy = bankFieldModalCopy(acctCheck)!;
                        showError(copy.title, copy.message, 'error');
                        return;
                      }
                    } else {
                      const destCopy = walletDestinationModalCopy(editingBeneficiary.msisdn)
                        || walletDestinationModalCopy(editingBeneficiary.identifier);
                      if (destCopy) {
                        showError(destCopy.title, destCopy.message, 'error');
                        return;
                      }
                    }
                    
                    const origin = editingAccountOrigin;
                    if (!origin || editingBeneficiary.accountType !== origin.accountType) {
                      showError(
                        'Validation Error',
                        'This edit updates the existing account. Use Add account to add a different account type.',
                        'warning'
                      );
                      return;
                    }

                    try {
                      const beneficiaryId = editingBeneficiary.id;
                      // Rename the card that was opened. Do not POST a new beneficiary:
                      // bank parties are keyed by a hash that includes the name, so create
                      // would open a second card.
                      await beneficiaryService.updateBeneficiary(beneficiaryId, {
                        name: editingBeneficiary.name.trim(),
                      });

                      if (editingBeneficiary.accountType === 'bank') {
                        const nextAccount = digitsOnly(editingBeneficiary.identifier);
                        const nextBank = (editingBeneficiary.bankName || '').trim();
                        const accountChanged = nextAccount !== origin.identifier
                          || nextBank !== (origin.bankName || '').trim();
                        if (accountChanged) {
                          if (!origin.paymentMethodId) {
                            await loadBeneficiaries();
                            showError('Error', 'This account could not be updated. Please try again.', 'error');
                            return;
                          }
                          const selectedBank = SA_BANKS.find(bank => bank.name === editingBeneficiary.bankName);
                          await beneficiaryService.addServiceToBeneficiary(Number(beneficiaryId), {
                            serviceType: 'bank',
                            serviceData: {
                              paymentMethodId: origin.paymentMethodId,
                              bankName: nextBank,
                              accountNumber: nextAccount,
                              accountType: 'cheque',
                              branchCode: selectedBank?.branchCode,
                            },
                          });
                        }
                      } else if (comparableMsisdn(editingBeneficiary.msisdn) !== origin.msisdn) {
                        if (!origin.paymentMethodId) {
                          await loadBeneficiaries();
                          showError('Error', 'This account could not be updated. Please try again.', 'error');
                          return;
                        }
                        await beneficiaryService.addServiceToBeneficiary(Number(beneficiaryId), {
                          serviceType: 'mymoolah',
                          serviceData: {
                            paymentMethodId: origin.paymentMethodId,
                            walletMsisdn: (editingBeneficiary.msisdn || '').trim(),
                          },
                        });
                      }

                      await loadBeneficiaries();
                      setShowEditBeneficiaryModal(false);
                      setEditingBeneficiary(null);
                      setEditingAccountOrigin(null);
                      showError('Success', 'Recipient updated successfully', 'info');
                    } catch (error: any) {
                      logError('SendMoneyPage', 'Failed to update beneficiary', error as Error);
                      showError('Error', error?.message || 'Failed to update recipient. Please try again.', 'error');
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Beneficiary Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveConfirmationModal}
        onClose={() => setShowRemoveConfirmationModal(false)}
        onConfirm={handleConfirmRemoveBeneficiary}
        title="Remove Recipient"
        message="Are you sure you want to remove"
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={beneficiaryToRemove?.name}
      />

      {/* Add Account Modal */}
      {addAccountTarget && (
        <AddAccountModal
          isOpen={showAddAccountModal}
          onClose={() => { setShowAddAccountModal(false); setAddAccountTarget(null); }}
          beneficiaryId={addAccountTarget.id}
          beneficiaryName={addAccountTarget.name}
          onSuccess={async () => {
            await loadBeneficiaries();
            showError('Success', `Account added to ${addAccountTarget.name} successfully.`, 'info');
          }}
        />
      )}

      {/* Remove Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveAccountModal}
        onClose={() => { setShowRemoveAccountModal(false); setRemoveAccountTarget(null); }}
        onConfirm={handleConfirmRemoveAccount}
        title="Remove Account"
        message={`Remove ${removeAccountTarget?.label || 'this account'} from`}
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={removeAccountTarget?.beneficiary.name}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal && errorModalData.message.length > 0}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
        type={errorModalData.type}
      />
    </div>
  );
}
